import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, numeric, integer, serial, timestamp, boolean, unique, uniqueIndex, index, jsonb, doublePrecision, bigint, date, type AnyPgColumn } from "drizzle-orm/pg-core";
import type { InferSelectModel } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ─── Sales Teams ──────────────────────────────────────────────────────────────
// Defined before users because users.teamId references this table.

export const salesTeams = pgTable("sales_teams", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  leaderId: varchar("leader_id"), // FK to users.id — set after users table
  region: text("region"),
  description: text("description"),
  goalType: text("goal_type"), // 'dealer_count' | 'mrr'
  goalValue: numeric("goal_value", { precision: 12, scale: 2 }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSalesTeamSchema = createInsertSchema(salesTeams).omit({ id: true, createdAt: true });
export type InsertSalesTeam = z.infer<typeof insertSalesTeamSchema>;
export type SalesTeam = typeof salesTeams.$inferSelect;

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  companyName: text("company_name"),
  email: text("email").unique(),
  role: text("role").notNull().default("subscriber"),
  stripeCustomerId: text("stripe_customer_id").unique(),
  stripeSubscriptionId: text("stripe_subscription_id").unique("uq_users_stripe_subscription_id"),
  subscriptionStatus: text("subscription_status"),
  createdAt: timestamp("created_at").defaultNow(),
  onboardedById: varchar("onboarded_by_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  onboardDate: timestamp("onboard_date"),
  commissionRate: numeric("commission_rate", { precision: 8, scale: 4 }),
  // Referral system
  referralCode: text("referral_code").unique(),
  // Flexible commission engine
  commissionType: text("commission_type"), // 'flat' | 'percent'
  commissionStructure: text("commission_structure"), // 'one-time' | 'residual'
  commissionInitialRate: numeric("commission_initial_rate", { precision: 10, scale: 4 }),
  commissionResidualRate: numeric("commission_residual_rate", { precision: 10, scale: 4 }),
  commissionResidualMonths: integer("commission_residual_months"), // null = indefinite
  payFrequency: text("pay_frequency"), // 'weekly' | 'biweekly' | 'monthly'
  goalType: text("goal_type"), // 'dealer_count' | 'mrr'
  goalValue: numeric("goal_value", { precision: 12, scale: 2 }),
  // Subscription cancellation timestamp
  canceledAt: timestamp("canceled_at"),
  // Multi-seat: owner userId this member belongs to (null = owner account)
  ownerUserId: varchar("owner_user_id").references((): AnyPgColumn => users.id, { onDelete: "cascade" }),
  // Dealer identity
  accountNumber: text("account_number").unique(),
  // Account status (Active, On Leave, Suspended, Terminated)
  accountStatus: text("account_status").notNull().default("active"),
  // Salesperson region/territory
  region: text("region"),
  // Salesperson phone for SMS broadcasts
  phone: text("phone"),
  // UI language preference: 'en' (default) | 'es'
  language: text("language").notNull().default("en"),
  // Immutable promo campaign applied at dealer signup (persisted for auditable commission history)
  appliedSalesPromoCampaignId: varchar("applied_sales_promo_campaign_id"),
  // 1099 contractor fields (stored, not exposed in list endpoints)
  taxId: text("tax_id"),
  mailingAddress: text("mailing_address"),
  contractStart: text("contract_start"), // YYYY-MM-DD
  contractEnd: text("contract_end"),     // YYYY-MM-DD
  // Salesperson streak: consecutive months hitting quota
  streakMonths: integer("streak_months").notNull().default(0),
  // Sales team membership
  teamId: varchar("team_id").references((): AnyPgColumn => salesTeams.id, { onDelete: "set null" }),
  // Last known payment method type for this dealer (populated by webhook)
  paymentMethodType: text("payment_method_type"), // 'card' | 'bank_account'
  // Terms & Conditions acceptance timestamp (nullable — existing users pre-date this requirement)
  termsAcceptedAt: timestamp("terms_accepted_at"),
  // ToS version string accepted at registration (e.g. "2026-06")
  tosVersion: text("tos_version"),
  // Super admin flag — super admins have elevated platform privileges
  isSuperAdmin: boolean("is_super_admin").notNull().default(false),
  // ─── Sales Leader fields ───────────────────────────────────────────────────
  // When a salesperson leads a team, they get a separate "team override"
  // commission track applied to deals closed by their team members.
  isLeader: boolean("is_leader").notNull().default(false),
  leaderCommissionType: text("leader_commission_type"), // 'flat' | 'percent'
  leaderCommissionRate: numeric("leader_commission_rate", { precision: 8, scale: 4 }),
  leaderCommissionStructure: text("leader_commission_structure"), // 'one-time' | 'residual'
  leaderCommissionInitialRate: numeric("leader_commission_initial_rate", { precision: 10, scale: 4 }),
  leaderCommissionResidualRate: numeric("leader_commission_residual_rate", { precision: 10, scale: 4 }),
  leaderCommissionResidualMonths: integer("leader_commission_residual_months"),
  leaderGoalType: text("leader_goal_type"), // 'dealer_count' | 'mrr'
  leaderGoalValue: numeric("leader_goal_value", { precision: 12, scale: 2 }),
  leaderPayFrequency: text("leader_pay_frequency"), // 'weekly' | 'biweekly' | 'monthly'
  // Salesperson permission role (FK to salesperson_roles) — controls dashboard access
  salespersonRoleId: varchar("salesperson_role_id"),
  // Display name (shown instead of username in charts/tables)
  name: text("name"),
  // Team member invitation flow
  inviteToken: text("invite_token"),           // hashed sha256 token
  inviteTokenExpiresAt: timestamp("invite_token_expires_at"),
  inviteAcceptedAt: timestamp("invite_accepted_at"),
  // Notification preferences
  marketplaceInquiryEmails: boolean("marketplace_inquiry_emails").notNull().default(true),
  // Trial / code metadata (Stripe stays authoritative for the actual trial end)
  trialSource: text("trial_source"),        // 'code' | 'manual'
  trialEndsAt: timestamp("trial_ends_at"),  // cached copy of Stripe trial_end for admin display
  codeUsed: text("code_used"),              // discount/trial/fixed-price code redeemed by this dealer
}, (t) => ({
  roleAccountStatusIdx: index("idx_users_role_account_status").on(t.role, t.accountStatus),
  onboardedByIdIdx: index("idx_users_onboarded_by_id").on(t.onboardedById),
}));

export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
  companyName: true,
  email: true,
  termsAcceptedAt: true,
  tosVersion: true,
});
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

// ─── ToS Acceptances ──────────────────────────────────────────────────────────
// Full history of every ToS version a user has explicitly accepted.
// One row per acceptance event (initial registration + any re-acceptances).

export const tosAcceptances = pgTable("tos_acceptances", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  tosVersion: text("tos_version").notNull(),
  acceptedAt: timestamp("accepted_at").notNull().defaultNow(),
  ipAddress: text("ip_address"),
});

export const insertTosAcceptanceSchema = createInsertSchema(tosAcceptances).omit({ id: true });
export type InsertTosAcceptance = z.infer<typeof insertTosAcceptanceSchema>;
export type TosAcceptance = typeof tosAcceptances.$inferSelect;

export const lenders = pgTable("lenders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  // ─── Term structure ────────────────────────────────────────────────────────
  period1Days: integer("period1_days"),
  period2Days: integer("period2_days"),
  period3Days: integer("period3_days"),
  period4Days: integer("period4_days"),
  aprRate: numeric("apr_rate", { precision: 8, scale: 4 }),
  feePeriod1: numeric("fee_period1", { precision: 10, scale: 2 }),
  feePeriod2: numeric("fee_period2", { precision: 10, scale: 2 }),
  feePeriod3: numeric("fee_period3", { precision: 10, scale: 2 }),
  feePeriod4: numeric("fee_period4", { precision: 10, scale: 2 }),
  curtailment1Type: text("curtailment1_type"),
  curtailment1Value: numeric("curtailment1_value", { precision: 10, scale: 4 }),
  curtailment2Type: text("curtailment2_type"),
  curtailment2Value: numeric("curtailment2_value", { precision: 10, scale: 4 }),
  curtailmentPct1: numeric("curtailment_pct_1", { precision: 8, scale: 4 }),
  curtailmentPct2: numeric("curtailment_pct_2", { precision: 8, scale: 4 }),
  curtailmentPct3: numeric("curtailment_pct_3", { precision: 8, scale: 4 }),
  avgUnitsPerMonth: numeric("avg_units_per_month", { precision: 8, scale: 2 }),
  // ─── Pricing type ──────────────────────────────────────────────────────────
  pricingType: text("pricing_type"),          // 'tiered' | 'fixed' | null (standard)
  fixedFeePerUnit: numeric("fixed_fee_per_unit", { precision: 10, scale: 2 }),
  // ─── Flex tier ─────────────────────────────────────────────────────────────
  flexEnabled: boolean("flex_enabled").default(false),
  flexThreshold: numeric("flex_threshold", { precision: 10, scale: 2 }),
  flexPeriod1Days: integer("flex_period1_days"),
  flexPeriod2Days: integer("flex_period2_days"),
  flexPeriod3Days: integer("flex_period3_days"),
  flexPeriod4Days: integer("flex_period4_days"),
  flexAprRate: numeric("flex_apr_rate", { precision: 8, scale: 4 }),
  flexFeePeriod1: numeric("flex_fee_period1", { precision: 10, scale: 2 }),
  flexFeePeriod2: numeric("flex_fee_period2", { precision: 10, scale: 2 }),
  flexFeePeriod3: numeric("flex_fee_period3", { precision: 10, scale: 2 }),
  flexFeePeriod4: numeric("flex_fee_period4", { precision: 10, scale: 2 }),
  flexCurtailment1Type: text("flex_curtailment1_type"),
  flexCurtailment1Value: numeric("flex_curtailment1_value", { precision: 10, scale: 4 }),
  flexCurtailment2Type: text("flex_curtailment2_type"),
  flexCurtailment2Value: numeric("flex_curtailment2_value", { precision: 10, scale: 4 }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertLenderSchema = createInsertSchema(lenders).omit({ id: true, createdAt: true, dealerId: true });
export type InsertLender = z.infer<typeof insertLenderSchema>;
export type Lender = typeof lenders.$inferSelect;

export const lenderTiers = pgTable("lender_tiers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  lenderId: varchar("lender_id").notNull().references(() => lenders.id, { onDelete: "cascade" }),
  threshold: numeric("threshold", { precision: 10, scale: 2 }).notNull(),
  sortOrder: integer("sort_order").default(0),
  period1Days: integer("period1_days"),
  period2Days: integer("period2_days"),
  period3Days: integer("period3_days"),
  period4Days: integer("period4_days"),
  aprRate: numeric("apr_rate", { precision: 8, scale: 4 }),
  feePeriod1: numeric("fee_period1", { precision: 10, scale: 2 }),
  feePeriod2: numeric("fee_period2", { precision: 10, scale: 2 }),
  feePeriod3: numeric("fee_period3", { precision: 10, scale: 2 }),
  feePeriod4: numeric("fee_period4", { precision: 10, scale: 2 }),
  curtailment1Type: text("curtailment1_type"),
  curtailment1Value: numeric("curtailment1_value", { precision: 10, scale: 4 }),
  curtailment2Type: text("curtailment2_type"),
  curtailment2Value: numeric("curtailment2_value", { precision: 10, scale: 4 }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertLenderTierSchema = createInsertSchema(lenderTiers).omit({ id: true, createdAt: true });
export type InsertLenderTier = z.infer<typeof insertLenderTierSchema>;
export type LenderTier = typeof lenderTiers.$inferSelect;
export type LenderWithTiers = Lender & { tiers: LenderTier[] };

export const floorplanEntries = pgTable("floorplan_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  statementId: varchar("statement_id").references(() => parsedStatements.id, { onDelete: "cascade" }),
  lenderId: varchar("lender_id").references(() => lenders.id, { onDelete: "set null" }),
  lenderName: text("lender_name"),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  entryDate: text("entry_date").notNull(),
  statedRate: numeric("stated_rate", { precision: 8, scale: 4 }),
  avgInventoryBalance: numeric("avg_inventory_balance", { precision: 12, scale: 2 }),
  totalInterestPaid: numeric("total_interest_paid", { precision: 12, scale: 2 }),
  totalFeesPaid: numeric("total_fees_paid", { precision: 12, scale: 2 }),
  avgDaysOnFloor: numeric("avg_days_on_floor", { precision: 8, scale: 2 }),
  curtailmentPct1: numeric("curtailment_pct_1", { precision: 8, scale: 4 }),
  curtailmentPct2: numeric("curtailment_pct_2", { precision: 8, scale: 4 }),
  curtailmentPct3: numeric("curtailment_pct_3", { precision: 8, scale: 4 }),
  floorplanFee: numeric("floorplan_fee", { precision: 10, scale: 2 }),
  curtailmentFee: numeric("curtailment_fee", { precision: 10, scale: 2 }),
  docFee: numeric("doc_fee", { precision: 10, scale: 2 }),
  titleFee: numeric("title_fee", { precision: 10, scale: 2 }),
  auditFee: numeric("audit_fee", { precision: 10, scale: 2 }),
  ancillaryFee1: numeric("ancillary_fee_1", { precision: 10, scale: 2 }),
  ancillaryFee1Label: text("ancillary_fee_1_label"),
  ancillaryFee2: numeric("ancillary_fee_2", { precision: 10, scale: 2 }),
  ancillaryFee2Label: text("ancillary_fee_2_label"),
  ancillaryFee3: numeric("ancillary_fee_3", { precision: 10, scale: 2 }),
  ancillaryFee3Label: text("ancillary_fee_3_label"),
  ancillaryFee4: numeric("ancillary_fee_4", { precision: 10, scale: 2 }),
  ancillaryFee4Label: text("ancillary_fee_4_label"),
  avgGrossProfitPerSale: numeric("avg_gross_profit_per_sale", { precision: 10, scale: 2 }),
  unitsSold: integer("units_sold"),
  periodDays: integer("period_days").default(30),
  creditLimit: numeric("credit_limit", { precision: 12, scale: 2 }),
  unusedLineAmountAvg: numeric("unused_line_amount_avg", { precision: 12, scale: 2 }),
  avgPurchasePrice: numeric("avg_purchase_price", { precision: 10, scale: 2 }),
  curtailmentPaidPrincipal: numeric("curtailment_paid_principal", { precision: 12, scale: 2 }),
  inventoryUnitsAvg: numeric("inventory_units_avg", { precision: 8, scale: 2 }),
  dealerWacc: numeric("dealer_wacc", { precision: 8, scale: 4 }),
  notes: text("notes"),
  effectiveApr: numeric("effective_apr", { precision: 8, scale: 4 }),
  totalCostPerUnit: numeric("total_cost_per_unit", { precision: 10, scale: 2 }),
  projectedAnnualCost: numeric("projected_annual_cost", { precision: 12, scale: 2 }),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  statementIdx: index("idx_floorplan_entries_statement_id").on(t.statementId),
}));

export const insertFloorplanEntrySchema = createInsertSchema(floorplanEntries).omit({ id: true, createdAt: true });
export type InsertFloorplanEntry = z.infer<typeof insertFloorplanEntrySchema>;
export type FloorplanEntry = typeof floorplanEntries.$inferSelect;

export const inventoryItems = pgTable("inventory_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  stockNumber: text("stock_number"),
  vin: text("vin"),
  year: integer("year"),
  make: text("make"),
  model: text("model"),
  trim: text("trim"),
  bodyStyle: text("body_style"),
  color: text("color"),
  purchasePrice: numeric("purchase_price", { precision: 10, scale: 2 }),
  salePrice: numeric("sale_price", { precision: 10, scale: 2 }),
  purchaseDate: text("purchase_date"),
  saleDate: text("sale_date"),
  daysInStock: integer("days_in_stock"),
  status: text("status").notNull().default("active"),
  lenderId: varchar("lender_id").references(() => lenders.id),
  flooredAmount: numeric("floored_amount", { precision: 10, scale: 2 }),
  createdAt: timestamp("created_at").defaultNow(),
  // Receivable-linked fields
  source: text("source").default("manual"),
  statementId: text("statement_id"),
  lenderName: text("lender_name"),
  daysOutstandingAtStmt: integer("days_outstanding_at_stmt"),
  statementDate: text("statement_date"),
  dailyInterestCost: numeric("daily_interest_cost", { precision: 10, scale: 4 }),
  dailyFeeCost: numeric("daily_fee_cost", { precision: 10, scale: 4 }),
  expectedSalePrice: numeric("expected_sale_price", { precision: 10, scale: 2 }),
  alertSent5Days: boolean("alert_sent_5_days").default(false),
  alertSentTurnTime: boolean("alert_sent_turn_time").default(false),
  alertSentGrossProfit: boolean("alert_sent_gross_profit").default(false),
  alertSentCustomDays: boolean("alert_sent_custom_days").default(false),
  fundingType: text("funding_type").default("floored"),
  costToCarryOverride: numeric("cost_to_carry_override", { precision: 10, scale: 2 }),
  lastPriceChangedAt: timestamp("last_price_changed_at"),
  reconCost: numeric("recon_cost", { precision: 10, scale: 2 }),
  daysAtFirstTracked: integer("days_at_first_tracked"),
  soldChannel: text("sold_channel"), // 'marketplace' | 'other' | null
  // ─── Durable vehicle identity (Potential Savings Engine, Phase 1) ─────────
  identityStatus: text("identity_status"), // 'vin_verified' | 'vin_missing' | 'unresolved'
  removedAt: timestamp("removed_at"),      // set when status becomes 'removed' (statement-missing lifecycle)
}, (t) => ({
  dealerVinIdx: uniqueIndex("idx_inventory_items_dealer_id_vin").on(t.dealerId, t.vin),
  statusIdx: index("idx_inventory_items_status").on(t.status),
  makeIdx: index("idx_inventory_items_make").on(t.make),
  modelIdx: index("idx_inventory_items_model").on(t.model),
  yearIdx: index("idx_inventory_items_year").on(t.year),
  salePriceIdx: index("idx_inventory_items_sale_price").on(t.salePrice),
}));

export const insertInventoryItemSchema = createInsertSchema(inventoryItems).omit({ id: true, createdAt: true });
export type InsertInventoryItem = z.infer<typeof insertInventoryItemSchema>;
export type InventoryItem = typeof inventoryItems.$inferSelect;

export const operationalMetrics = pgTable("operational_metrics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  periodMonth: text("period_month").notNull(),
  totalUnitsSold: integer("total_units_sold"),
  totalRevenue: numeric("total_revenue", { precision: 12, scale: 2 }),
  totalFloorplanCost: numeric("total_floorplan_cost", { precision: 12, scale: 2 }),
  avgDaysToSell: numeric("avg_days_to_sell", { precision: 8, scale: 2 }),
  totalGrossProfit: numeric("total_gross_profit", { precision: 12, scale: 2 }),
  reconCost: numeric("recon_cost", { precision: 10, scale: 2 }),
  advertisingCost: numeric("advertising_cost", { precision: 10, scale: 2 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertOperationalMetricsSchema = createInsertSchema(operationalMetrics).omit({ id: true, createdAt: true });
export type InsertOperationalMetrics = z.infer<typeof insertOperationalMetricsSchema>;
export type OperationalMetrics = typeof operationalMetrics.$inferSelect;

export const parsedStatements = pgTable("parsed_statements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  lenderId: varchar("lender_id").references(() => lenders.id, { onDelete: "set null" }),
  fileName: text("file_name").notNull(),
  filePath: text("file_path"),
  fileType: text("file_type").notNull(),
  extractionMethod: text("extraction_method"),
  lenderName: text("lender_name"),
  lenderKey: text("lender_key"),
  statementPeriodStart: text("statement_period_start"),
  statementPeriodEnd: text("statement_period_end"),
  statementMonth: integer("statement_month"),
  statementYear: integer("statement_year"),
  totalInterestPaid: numeric("total_interest_paid", { precision: 12, scale: 2 }),
  totalFeesPaid: numeric("total_fees_paid", { precision: 12, scale: 2 }),
  averageBalance: numeric("average_balance", { precision: 12, scale: 2 }),
  unitsSold: integer("units_sold"),
  periodDays: integer("period_days"),
  avgDaysInStock: numeric("avg_days_in_stock", { precision: 8, scale: 2 }),
  feeAdmin: numeric("fee_admin", { precision: 10, scale: 2 }),
  feeAudit: numeric("fee_audit", { precision: 10, scale: 2 }),
  feeCurtailment: numeric("fee_curtailment", { precision: 10, scale: 2 }),
  feeDoc: numeric("fee_doc", { precision: 10, scale: 2 }),
  feeTitle: numeric("fee_title", { precision: 10, scale: 2 }),
  feeOther: numeric("fee_other", { precision: 10, scale: 2 }),
  receivablesJson: text("receivables_json"),
  confidenceJson: text("confidence_json"),
  warningsJson: text("warnings_json"),
  accuracyCheckJson: text("accuracy_check_json"),
  notes: text("notes"),
  status: text("status").notNull().default("pending_review"),
  fileHash: text("file_hash"),
  extractionDiagnosticsJson: text("extraction_diagnostics_json"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertParsedStatementSchema = createInsertSchema(parsedStatements).omit({ id: true, createdAt: true });
export type InsertParsedStatement = z.infer<typeof insertParsedStatementSchema>;
export type ParsedStatement = typeof parsedStatements.$inferSelect;

export const dealerProfiles = pgTable("dealer_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  dealershipName: text("dealership_name"),
  address: text("address"),
  dealerType: text("dealer_type"),
  avgUnitsOnLot: numeric("avg_units_on_lot", { precision: 8, scale: 2 }),
  lotCapacity: numeric("lot_capacity", { precision: 8, scale: 2 }),
  avgSalesPerMonth: numeric("avg_sales_per_month", { precision: 8, scale: 2 }),
  avgGrossProfitPerUnit: numeric("avg_gross_profit_per_unit", { precision: 10, scale: 2 }),
  // avgVehicleValue: collected in the onboarding wizard (Phase 1). Used in:
  //   - frontend break-even / SLA calculations (sla.tsx)
  //   - revenue estimation on the operations dashboard (operations.tsx)
  //   - goal tracking display (goals.tsx)
  //   - floorplan scenario modelling (floorplan.tsx)
  //   - lender comparison APR calcs (server/routes/lenders.ts)
  //   - assessment generator and DEAN chat context
  // Do NOT remove without auditing all of those call sites.
  avgVehicleValue: numeric("avg_vehicle_value", { precision: 10, scale: 2 }),
  avgMonthlyRevenue: numeric("avg_monthly_revenue", { precision: 12, scale: 2 }),
  // Dealer-entered manual override for monthly revenue on the Team page. When set,
  // it takes priority over the auto-derived chain (tax return → financial statement →
  // op analytics → profile → onboarding) in the team-metrics endpoint. Null = no override.
  manualMonthlyRevenue: numeric("manual_monthly_revenue", { precision: 12, scale: 2 }),
  monthlyOperatingExpenses: numeric("monthly_operating_expenses", { precision: 12, scale: 2 }),
  avgTurnDays: numeric("avg_turn_days", { precision: 8, scale: 2 }),
  avgReconDays: numeric("avg_recon_days", { precision: 8, scale: 2 }),
  goalAvgUnitsOnLot: numeric("goal_avg_units_on_lot", { precision: 8, scale: 2 }),
  goalLotCapacity: numeric("goal_lot_capacity", { precision: 8, scale: 2 }),
  goalAvgSalesPerMonth: numeric("goal_avg_sales_per_month", { precision: 8, scale: 2 }),
  goalAvgGrossProfitPerUnit: numeric("goal_avg_gross_profit_per_unit", { precision: 10, scale: 2 }),
  goalAvgVehicleValue: numeric("goal_avg_vehicle_value", { precision: 10, scale: 2 }),
  goalAvgTurnDays: numeric("goal_avg_turn_days", { precision: 8, scale: 2 }),
  goalAvgMonthlyRevenue: numeric("goal_avg_monthly_revenue", { precision: 12, scale: 2 }),
  goalMonthlyOperatingExpenses: numeric("goal_monthly_operating_expenses", { precision: 12, scale: 2 }),
  // Set when the dealer permanently dismisses the first-visit goal setup prompt on /goals
  goalsSetupDismissedAt: timestamp("goals_setup_dismissed_at"),
  notifyEmail: text("notify_email"),
  notifyByEmail: boolean("notify_by_email").default(false),
  alertDaysBeforeGoal: integer("alert_days_before_goal").default(5),
  alertCustomAgingDays: integer("alert_custom_aging_days"),
  onboardingCompleted: boolean("onboarding_completed").default(false),
  // ─── Phase 1 extended fields ─────────────────────────────────────────────
  numRooftops: integer("num_rooftops"),
  inventoryType: text("inventory_type"), // 'used_retail' | 'wholesale' | 'bhph' | 'franchise' (legacy: 'used', 'mixed' → mapped to 'used_retail' on read)
  totalHeadcount: integer("total_headcount"),
  // ─── Phase 2 DMS fields ──────────────────────────────────────────────────
  dmsSystem: text("dms_system"),
  dmsMonthlyCost: numeric("dms_monthly_cost", { precision: 10, scale: 2 }),
  // ─── Phase 3 extended fields ─────────────────────────────────────────────
  lendingPrimePct: numeric("lending_prime_pct", { precision: 5, scale: 2 }),
  lendingSubprimePct: numeric("lending_subprime_pct", { precision: 5, scale: 2 }),
  lendingBhphPct: numeric("lending_bhph_pct", { precision: 5, scale: 2 }),
  lendingCashPct: numeric("lending_cash_pct", { precision: 5, scale: 2 }),
  primaryLenderNames: text("primary_lender_names"),
  primaryLenders: jsonb("primary_lenders").$type<Array<{ name: string; type: "prime" | "subprime" | "mix"; isPrimary: boolean }>>(), // structured lender entries
  // ─── Phase 4 extended fields ─────────────────────────────────────────────
  avgFrontEndGross: numeric("avg_front_end_gross", { precision: 10, scale: 2 }),
  avgBackEndGross: numeric("avg_back_end_gross", { precision: 10, scale: 2 }),
  avgFixedCosts: numeric("avg_fixed_costs", { precision: 12, scale: 2 }),
  avgVariableCostPerUnit: numeric("avg_variable_cost_per_unit", { precision: 10, scale: 2 }),
  avgReconCost: numeric("avg_recon_cost", { precision: 10, scale: 2 }),
  avgTransportFee: numeric("avg_transport_fee", { precision: 10, scale: 2 }),
  // ─── Phase 5 extended fields ─────────────────────────────────────────────
  avgFloorplanRate: numeric("avg_floorplan_rate", { precision: 6, scale: 4 }),
  targetTurnDays: numeric("target_turn_days", { precision: 8, scale: 2 }),
  // ─── Phase 6 extended fields ─────────────────────────────────────────────
  sourcingAuctionPct: numeric("sourcing_auction_pct", { precision: 5, scale: 2 }),
  sourcingTradeInPct: numeric("sourcing_trade_in_pct", { precision: 5, scale: 2 }),
  sourcingPrivatePartyPct: numeric("sourcing_private_party_pct", { precision: 5, scale: 2 }),
  sourcingDealerToDealerPct: numeric("sourcing_dealer_to_dealer_pct", { precision: 5, scale: 2 }),
  primaryAuctionLanes: text("primary_auction_lanes"),
  avgDaysPurchaseToFrontline: numeric("avg_days_purchase_to_frontline", { precision: 8, scale: 2 }),
  // ─── Phase 7 extended fields ─────────────────────────────────────────────
  cashReservesRange: text("cash_reserves_range"), // '<50k' | '50-150k' | '150-500k' | '500k+'
  hasLineOfCredit: boolean("has_line_of_credit"),
  lineOfCreditRange: text("line_of_credit_range"),
  hasNonFloorplanDebt: boolean("has_non_floorplan_debt"),
  nonFloorplanDebtMonthly: numeric("non_floorplan_debt_monthly", { precision: 12, scale: 2 }),
  bhphReceivablesRange: text("bhph_receivables_range"),
  // ─── Phase 8 extended fields ─────────────────────────────────────────────
  monthlyLeadVolume: integer("monthly_lead_volume"),
  leadToSaleConversionPct: numeric("lead_to_sale_conversion_pct", { precision: 5, scale: 2 }),
  leadSourcesRanked: text("lead_sources_ranked"),
  tracksRepeatReferral: boolean("tracks_repeat_referral"),
  repeatReferralPct: numeric("repeat_referral_pct", { precision: 5, scale: 2 }),
  openaiApiKey: text("openai_api_key"),
  anthropicApiKey: text("anthropic_api_key"),
  // ─── Location fields (required for hierarchy price resolution) ────────────
  zipCode: text("zip_code"),
  stateCode: text("state_code"),
  city: text("city"),
  // ─── SLA alert goals ─────────────────────────────────────────────────────
  goalMaxHoldingCost: numeric("goal_max_holding_cost", { precision: 10, scale: 2 }),
  goalPriceStagnationDays: integer("goal_price_stagnation_days"),
  // ─── Per-alert enable flags ───────────────────────────────────────────────
  alertAgingEnabled: boolean("alert_aging_enabled").default(true),
  alertHoldingCostEnabled: boolean("alert_holding_cost_enabled").default(true),
  alertPriceStagnationEnabled: boolean("alert_price_stagnation_enabled").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertDealerProfileSchema = createInsertSchema(dealerProfiles).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDealerProfile = z.infer<typeof insertDealerProfileSchema>;
export type DealerProfile = typeof dealerProfiles.$inferSelect;

export const passwordResetTokens = pgTable("password_reset_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  tokenHash: text("token_hash").notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const opAnalytics = pgTable("op_analytics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  lastSavedAt: timestamp("last_saved_at").notNull().defaultNow(),
  inputJson: text("input_json").notNull().default("{}"),
  outputJson: text("output_json"),
  taxSourceId: varchar("tax_source_id"),
  taxSourceVersion: text("tax_source_version"),
  taxSourceProvisional: boolean("tax_source_provisional").notNull().default(false),
});

export const insertOpAnalyticsSchema = createInsertSchema(opAnalytics).omit({ id: true });
export type InsertOpAnalytics = z.infer<typeof insertOpAnalyticsSchema>;
export type OpAnalytics = typeof opAnalytics.$inferSelect;

export const dealerVendors = pgTable("dealer_vendors", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  category: text("category"),
  monthlyCost: numeric("monthly_cost", { precision: 10, scale: 2 }),
  leadsPerMonth: integer("leads_per_month"),
  salesPerMonth: integer("sales_per_month"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDealerVendorSchema = createInsertSchema(dealerVendors).omit({ id: true, createdAt: true });
export type InsertDealerVendor = z.infer<typeof insertDealerVendorSchema>;
export type DealerVendor = typeof dealerVendors.$inferSelect;

export const dealerEmployees = pgTable("dealer_employees", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  count: integer("count").notNull().default(1),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDealerEmployeeSchema = createInsertSchema(dealerEmployees).omit({ id: true, createdAt: true });
export type InsertDealerEmployee = z.infer<typeof insertDealerEmployeeSchema>;
export type DealerEmployee = typeof dealerEmployees.$inferSelect;

export const dealerOnboardingProgress = pgTable("dealer_onboarding_progress", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  entryMethod: text("entry_method").notNull().default("manual"), // 'manual' | 'statement'
  currentPhase: integer("current_phase").notNull().default(0),
  completedPhases: text("completed_phases").notNull().default("[]"), // JSON array of phase numbers
  phaseSnapshots: text("phase_snapshots").notNull().default("{}"), // JSON object: phase -> data
  statementSourcedFields: text("statement_sourced_fields").notNull().default("[]"), // JSON array of field names
  confidenceScore: numeric("confidence_score", { precision: 5, scale: 2 }),
  confidenceBreakdown: text("confidence_breakdown"), // JSON
  statementId: varchar("statement_id").references(() => parsedStatements.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertDealerOnboardingProgressSchema = createInsertSchema(dealerOnboardingProgress).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDealerOnboardingProgress = z.infer<typeof insertDealerOnboardingProgressSchema>;
export type DealerOnboardingProgress = typeof dealerOnboardingProgress.$inferSelect;

export const conversations = pgTable("conversations", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull().default("New Chat"),
  documentContext: text("document_context"),
  documentType: text("document_type"),
  summary: text("summary"),
  documentSourceType: text("document_source_type"),
  documentSourceId: varchar("document_source_id"),
  documentSourceVersion: text("document_source_version"),
  documentSourceHash: text("document_source_hash"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertConversationSchema = z.object({
  userId: z.string(),
  title: z.string().optional(),
  documentContext: z.string().nullable().optional(),
  documentType: z.string().nullable().optional(),
});
export type InsertConversation = z.infer<typeof insertConversationSchema>;
export type Conversation = typeof conversations.$inferSelect;

export const dealerMemory = pgTable("dealer_memory", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  memoryKey: text("memory_key").notNull(),
  memoryValue: text("memory_value").notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertDealerMemorySchema = createInsertSchema(dealerMemory);
export type InsertDealerMemory = z.infer<typeof insertDealerMemorySchema>;
export type DealerMemory = typeof dealerMemory.$inferSelect;

export const vendors = pgTable("vendors", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  category: text("category").notNull(),
  description: text("description"),
  websiteUrl: text("website_url"),
  sizeFit: text("size_fit"),
  affordabilityScore: integer("affordability_score").default(5),
  technologyScore: integer("technology_score").default(5),
  valueScore: integer("value_score").default(5),
  supportScore: integer("support_score").default(5),
  sizeFitScore: integer("size_fit_score").default(5),
  submittedByUserId: varchar("submitted_by_user_id").references(() => users.id, { onDelete: "set null" }),
  isCommunityAdded: boolean("is_community_added").default(false),
  isApproved: boolean("is_approved").default(false),
  googlePlaceId: text("google_place_id"),
  googleRating: numeric("google_rating", { precision: 3, scale: 1 }),
  lastSeededAt: timestamp("last_seeded_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertVendorSchema = createInsertSchema(vendors).omit({ id: true, createdAt: true });
export type InsertVendor = z.infer<typeof insertVendorSchema>;
export type Vendor = typeof vendors.$inferSelect;

export const vendorSeeds = pgTable("vendor_seeds", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runAt: timestamp("run_at").defaultNow(),
  vendorsAdded: integer("vendors_added").notNull().default(0),
  duplicatesSkipped: integer("duplicates_skipped").notNull().default(0),
  status: text("status").notNull().default("success"),
  errorMessage: text("error_message"),
});

export const insertVendorSeedSchema = createInsertSchema(vendorSeeds).omit({ id: true, runAt: true });
export type InsertVendorSeed = z.infer<typeof insertVendorSeedSchema>;
export type VendorSeed = typeof vendorSeeds.$inferSelect;

export const vendorReviews = pgTable("vendor_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  vendorId: varchar("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  stars: integer("stars").notNull(),
  comment: text("comment"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  vendorUserUnique: unique("vendor_reviews_vendor_user_unique").on(table.vendorId, table.userId),
}));

export const insertVendorReviewSchema = createInsertSchema(vendorReviews).omit({ id: true, createdAt: true });
export type InsertVendorReview = z.infer<typeof insertVendorReviewSchema>;
export type VendorReview = typeof vendorReviews.$inferSelect;

export type VendorWithReviews = Vendor & {
  reviews: (VendorReview & { reviewerName: string })[];
  averageStars: number | null;
  compositeScore: number;
  myReview?: VendorReview | null;
};

export type DeanRecordReference = {
  kind: "inventory" | "floorplan_lender" | "bank_statement" | "bank_transaction" | "saved_tax_return" | "dealer_vendor";
  id: string;
  dealerId: string;
};

export type DeanPublicationEvidenceField = {
  field: string;
  value: number;
};

export type DeanPublicationEvidenceAggregateRequest = {
  dataset: "inventory" | "bank_transactions" | "floorplan_statements" | "floorplan_receivables" | "financial_statements";
  operation: "count" | "sum" | "average" | "min" | "max";
  field?: string;
  groupBy?: string;
  status?: string;
  minDaysInStockToday?: number;
  maxDaysInStockToday?: number;
  lender?: string;
  fundingType?: string;
  category?: string;
  dateField?: string;
  dateFrom?: string;
  dateTo?: string;
  groupOffset?: number;
  groupLimit?: number;
};

export type DeanPublicationEvidenceSourceLabelPolicy =
  | "unverified_upload"
  | "manual_entry"
  | "system_record"
  | "estimated_not_realized";

export type DeanPublicationEvidenceRecord = {
  id: string;
  version: string;
  source?: string;
  capability?: string;
  sourceLabelPolicy?: DeanPublicationEvidenceSourceLabelPolicy;
  sourceGeneration?: string;
  period: string;
  verificationStatus: "verified" | "unverified" | "conflicted";
  conflict: boolean;
  fields: DeanPublicationEvidenceField[];
  aggregateRequest?: DeanPublicationEvidenceAggregateRequest;
  aggregateSourceGeneration?: string;
};

export type DeanPublicationEvidenceCalculationOperand =
  | { type: "evidence"; recordId: string; field: string }
  | { type: "literal"; value: number }
  | {
      type: "result_set";
      source?: string;
      capability?: string;
      period?: string;
      field: string;
      filters?: Array<{ field: string; operator: "eq" | "ne" | "gt" | "gte" | "lt" | "lte"; value: number | string | boolean }>;
    };

export type DeanPublicationEvidenceCalculation = {
  callId: string;
  operation: string;
  operands: DeanPublicationEvidenceCalculationOperand[];
  result: number;
  verificationStatus: "verified" | "unverified";
  resultSetRecordIds?: string[];
  members?: Array<{
    recordId: string;
    field: string;
    version: string;
    value: number;
    included: boolean;
  }>;
};

/**
 * Private, server-only citation proof stored with an assistant message.
 * Values are provenance snapshots, not authority: continuity may use them only
 * after reloading the matching dealer-owned records at their saved versions.
 */
export type DeanPublicationEvidenceEnvelope = {
  schemaVersion: 1;
  dealerId: string;
  records: DeanPublicationEvidenceRecord[];
  calculations: DeanPublicationEvidenceCalculation[];
};

export const messages = pgTable("messages", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  conversationId: integer("conversation_id").notNull().references(() => conversations.id, { onDelete: "cascade" }),
  exchangeId: uuid("exchange_id"),
  role: text("role").notNull(),
  content: text("content").notNull(),
  recordReference: jsonb("record_reference").$type<DeanRecordReference>(),
  publicationEvidence: jsonb("publication_evidence").$type<DeanPublicationEvidenceEnvelope>(),
  summarized: boolean("summarized").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  conversationExchangeIdx: index("idx_messages_conversation_exchange").on(table.conversationId, table.exchangeId),
}));

export const insertMessageSchema = z.object({
  conversationId: z.number(),
  exchangeId: z.string().uuid().optional(),
  role: z.string(),
  content: z.string(),
  recordReference: z.object({
    kind: z.enum(["inventory", "floorplan_lender", "bank_statement", "bank_transaction", "saved_tax_return", "dealer_vendor"]),
    id: z.string(),
    dealerId: z.string(),
  }).nullable().optional(),
});
export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type Message = typeof messages.$inferSelect;

// ─── Bank Health: Bank Statements ────────────────────────────────────────────

export const bankStatements = pgTable("bank_statements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  fileName: text("file_name").notNull(),
  filePath: text("file_path"),
  fileType: text("file_type").notNull(),
  accountPeriodStart: text("account_period_start"),
  accountPeriodEnd: text("account_period_end"),
  openingBalance: numeric("opening_balance", { precision: 14, scale: 2 }),
  closingBalance: numeric("closing_balance", { precision: 14, scale: 2 }),
  totalCredits: numeric("total_credits", { precision: 14, scale: 2 }),
  totalDebits: numeric("total_debits", { precision: 14, scale: 2 }),
  statedCredits: numeric("stated_credits", { precision: 14, scale: 2 }),
  statedDebits: numeric("stated_debits", { precision: 14, scale: 2 }),
  serviceFees: numeric("service_fees", { precision: 14, scale: 2 }),
  checksAmount: numeric("checks_amount", { precision: 14, scale: 2 }),
  // Headline "money out" override (dollars) for a uniquely-resolved unknown-bank
  // FOLDED statement — the reconciled debits + service-fee target with the
  // double-counted Checks subtotal excluded. NULL for every other case (separate,
  // ambiguous, known banks, multi-account), where the additive
  // statedDebits + checksAmount + serviceFees headline is already correct.
  moneyOutOverride: numeric("money_out_override", { precision: 14, scale: 2 }),
  notableItemsJson: text("notable_items_json"),
  accountsJson: text("accounts_json"),
  confidenceJson: text("confidence_json"),
  accuracyScore: numeric("accuracy_score", { precision: 5, scale: 2 }),
  warningsJson: text("warnings_json"),
  avgDailyBalance: numeric("avg_daily_balance", { precision: 14, scale: 2 }),
  // Provenance of avgDailyBalance: "daily_balance_summary_table" (code-computed
  // from a complete, closing-reconciled per-day table — incl. the focused recovery
  // pass), "running_balances" (code-computed from per-row running balances),
  // "model_estimate" (the model's own unverified figure), or null when no average.
  avgDailyBalanceSource: text("avg_daily_balance_source"),
  // True ONLY when the average is backed by persisted source rows AND a stored
  // computation (sum, denominator, per-day expansion) — i.e. the figure can be
  // re-derived and audited row-by-row. A closing-balance match alone is NOT
  // sufficient. Dealer-confirmed averages are treated as verified at the UI
  // layer. model_estimate / legacy null-source averages stay false.
  avgDailyBalanceVerified: boolean("avg_daily_balance_verified").notNull().default(false),
  // Full audit trail for the average daily balance so the figure is provable
  // after the fact. JSON shape (see AvgDailyBalanceAudit in bankStatementPipeline):
  //   { source, verified, computedAvg, total, days, sourceRows[], anchors[],
  //     expansion[], method, computedAt }
  // sourceRows = the printed Daily Balance Summary rows; anchors = date→balance
  // points actually used; expansion = the per-calendar-day carry-forward series
  // summed into the mean. Null for legacy records and records with no average.
  avgDailyBalanceAuditJson: text("avg_daily_balance_audit_json"),
  transactionExtractionFailed: boolean("transaction_extraction_failed").notNull().default(false),
  transactionExtractionError: text("transaction_extraction_error"),
  needsReview: boolean("needs_review").notNull().default(false),
  reviewMessage: text("review_message"),
  repairAttempted: boolean("repair_attempted").notNull().default(false),
  validationChecksJson: text("validation_checks_json"),
  fallbackPath: text("fallback_path"),
  // PHASE 14C.2 — backend-computed readiness states persisted as JSON so the UI
  // tier helper uses backend truth when present. Nullable: old records and legacy
  // (non-resolved_candidate) extractions leave this null and the frontend falls
  // back to balance-equation inference. Shape:
  //   { summaryMetricsReady, transactionAnalyticsReady, runningBalanceAnalyticsReady:
  //       "ready"|"blocked"|"not_available"|"unknown",
  //     validationDecisionSource: string|null, reviewReasons: string[] }
  // Never stores sensitive bank figures — only readiness state labels + reasons.
  bankReadinessJson: text("bank_readiness_json"),
  // Dealer-corrected summary figures for a Review Required statement. When the AI
  // cannot verify the printed totals, the dealer can confirm the true headline
  // money-in / money-out / average balance so the statement still feeds the
  // summary-level Bank Health charts (transaction-level charts stay limited).
  // dealerVerifiedAt being non-null marks the summary as dealer-confirmed.
  dealerCorrectedCredits: numeric("dealer_corrected_credits", { precision: 14, scale: 2 }),
  dealerCorrectedDebits: numeric("dealer_corrected_debits", { precision: 14, scale: 2 }),
  dealerCorrectedAvgBalance: numeric("dealer_corrected_avg_balance", { precision: 14, scale: 2 }),
  dealerVerifiedAt: timestamp("dealer_verified_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBankStatementSchema = createInsertSchema(bankStatements).omit({ id: true, createdAt: true });
export type InsertBankStatement = z.infer<typeof insertBankStatementSchema>;
export type BankStatement = typeof bankStatements.$inferSelect;

// ─── Bank Health: Bank Transactions ──────────────────────────────────────────

export const bankTransactions = pgTable("bank_transactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  bankStatementId: varchar("bank_statement_id").references(() => bankStatements.id, { onDelete: "cascade" }),
  transactionDate: text("transaction_date"),
  payee: text("payee"),
  description: text("description"),
  amount: numeric("amount", { precision: 14, scale: 2 }),
  type: text("type"), // 'credit' | 'debit' | 'unknown'
  category: text("category"),
  accountLabel: text("account_label"),
  confidence: numeric("confidence", { precision: 5, scale: 4 }),
  runningBalance: numeric("running_balance", { precision: 14, scale: 2 }),
  manuallyCorrected: boolean("manually_corrected").default(false),
  // Returned/rejected-item handling. A bounced check (NSF) is printed on the
  // statement on BOTH sides but excluded from the printed deposit/withdrawal
  // totals. We keep the row visible but flag it so reconciliation, money-out,
  // average-balance and category math skip it. Defaults preserve legacy behavior
  // for every existing row (posted + included).
  postingStatus: text("posting_status").default("posted"), // 'posted'|'returned'|'rejected'|'informational'|'unknown'
  includedInStatementTotals: boolean("included_in_statement_totals").default(true),
  exclusionReason: text("exclusion_reason"),
  linkedTransactionId: varchar("linked_transaction_id"),
  // Strict normalization provenance is nullable so historical rows remain honest:
  // null means the row predates durable normalization evidence.
  rawTypeJson: text("raw_type_json"),
  rawAmountJson: text("raw_amount_json"),
  normalizedDirection: text("normalized_direction"), // 'credit'|'debit'|'unknown'
  normalizedAmount: numeric("normalized_amount", { precision: 14, scale: 2 }),
  directionStatus: text("direction_status"),
  amountStatus: text("amount_status"),
  originalSignedAmountCents: numeric("original_signed_amount_cents", { precision: 18, scale: 0 }),
  normalizationNeedsReview: boolean("normalization_needs_review"),
  normalizationReviewReason: text("normalization_review_reason"),
  normalizationEligibleForTotals: boolean("normalization_eligible_for_totals"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBankTransactionSchema = createInsertSchema(bankTransactions).omit({ id: true, createdAt: true });
export type InsertBankTransaction = z.infer<typeof insertBankTransactionSchema>;
export type BankTransaction = typeof bankTransactions.$inferSelect;

// ─── Bank Health: Merchant Category Rules ────────────────────────────────────

export const transactionCategoryRules = pgTable("transaction_category_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  normalizedPayee: text("normalized_payee").notNull(),
  category: text("category").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTransactionCategoryRuleSchema = createInsertSchema(transactionCategoryRules).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTransactionCategoryRule = z.infer<typeof insertTransactionCategoryRuleSchema>;
export type TransactionCategoryRule = typeof transactionCategoryRules.$inferSelect;

// ─── Document Repository ─────────────────────────────────────────────────────

export const DOCUMENT_CATEGORIES = [
  "Lender Statements",
  "Tax Returns / P&L",
  "Bank Statements",
  "Floor Plan Agreements",
  "Vendor Agreements",
  "Compliance / DMV",
  "Insurance",
  "General Business",
] as const;

export type DocumentCategory = typeof DOCUMENT_CATEGORIES[number];

export const dealerDocuments = pgTable("dealer_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  fileName: text("file_name").notNull(),
  fileType: text("file_type").notNull(),
  category: text("category").notNull(),
  description: text("description"),
  tags: text("tags").array(),
  filePath: text("file_path"),
  fileSize: integer("file_size"),
  extractedText: text("extracted_text"),
  sourceVersion: text("source_version").notNull().default("1"),
  contentHash: text("content_hash"),
  lifecycleStatus: text("lifecycle_status").notNull().default("unknown"),
  extractionStatus: text("extraction_status").notNull().default("unknown"),
  contentOrigin: text("content_origin").notNull().default("unknown"),
  effectivePeriod: text("effective_period"),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
});

export const insertDealerDocumentSchema = createInsertSchema(dealerDocuments).omit({ id: true, uploadedAt: true });
export type InsertDealerDocument = z.infer<typeof insertDealerDocumentSchema>;
export type DealerDocument = typeof dealerDocuments.$inferSelect;

// ─── Async Job Table ──────────────────────────────────────────────────────────
// Persists background job state so jobs survive server restarts.
// Used for tax-return extraction and future long-running jobs.

export const asyncJobs = pgTable("async_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  jobType: text("job_type").notNull(), // 'tax_return' | 'floorplan'
  status: text("status").notNull().default("processing"), // 'processing' | 'queued' | 'done' | 'error'
  progressJson: text("progress_json"),
  resultJson: text("result_json"),
  errorMessage: text("error_message"),
  metaJson: text("meta_json"), // job-type-specific metadata (original filename, cloudPath, etc.)
  fileHash: text("file_hash"), // SHA-256 of uploaded file for deduplication
  sourceId: varchar("source_id"),
  sourceVersion: text("source_version"),
  stepTimingsMs: jsonb("step_timings_ms"), // per-step elapsed time map for floorplan extraction
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertAsyncJobSchema = createInsertSchema(asyncJobs).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertAsyncJob = z.infer<typeof insertAsyncJobSchema>;
export type AsyncJob = typeof asyncJobs.$inferSelect;

// ─── Pending Uploads (direct-to-storage presigned uploads) ────────────────────
// One row per presigned PUT the server hands out. The server owns the object
// key (clients can never choose or finalize arbitrary storage paths); finalize
// endpoints look up the trusted path by uploadId only. Unfinalized rows expire
// after PENDING_UPLOAD_TTL (30 min) and are swept along with their objects.
export const pendingUploads = pgTable("pending_uploads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull(),
  documentType: text("document_type").notNull(), // floorplan|bank_statement|tax_return|financial_statement|dealer_document|onboarding|inventory_pdf
  objectPath: text("object_path").notNull(),     // "/bucket/prefix/pending/{dealerId}/{id}/document.ext" — server-generated
  originalName: text("original_name").notNull(), // sanitized display name; NEVER used as a storage key or temp path
  contentType: text("content_type").notNull(),
  sizeLimitBytes: integer("size_limit_bytes").notNull(),
  status: text("status").notNull().default("pending"), // 'pending' | 'finalized'
  finalizedPipeline: text("finalized_pipeline"),
  finalizedJobId: text("finalized_job_id"),      // async_jobs id or created record id — repeated finalize returns this
  finalCloudPath: text("final_cloud_path"),      // permanent object path after the pending→uploads move
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPendingUploadSchema = createInsertSchema(pendingUploads).omit({ createdAt: true });
export type InsertPendingUpload = z.infer<typeof insertPendingUploadSchema>;
export type PendingUpload = typeof pendingUploads.$inferSelect;

// ─── Financial Statements ─────────────────────────────────────────────────────

export const financialStatements = pgTable("financial_statements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  statementType: text("statement_type").notNull().default("pl"),
  periodStart: text("period_start").notNull(),
  periodEnd: text("period_end").notNull(),
  sourceType: text("source_type").notNull().default("legacy_unknown"),
  periodPrecision: text("period_precision").notNull().default("unknown"),
  asOfDate: date("as_of_date", { mode: "string" }),
  periodYear: integer("period_year"),
  dealershipName: text("dealership_name"),
  address: text("address"),
  businessState: text("business_state"),
  totalRevenue: numeric("total_revenue", { precision: 14, scale: 2 }),
  costOfGoodsSold: numeric("cost_of_goods_sold", { precision: 14, scale: 2 }),
  grossProfit: numeric("gross_profit", { precision: 14, scale: 2 }),
  unitsSold: integer("units_sold"),
  avgFrontEndGross: numeric("avg_front_end_gross", { precision: 10, scale: 2 }),
  avgBackEndGross: numeric("avg_back_end_gross", { precision: 10, scale: 2 }),
  totalOperatingExpenses: numeric("total_operating_expenses", { precision: 14, scale: 2 }),
  payroll: numeric("payroll", { precision: 12, scale: 2 }),
  rentMortgage: numeric("rent_mortgage", { precision: 12, scale: 2 }),
  utilities: numeric("utilities", { precision: 12, scale: 2 }),
  advertising: numeric("advertising", { precision: 12, scale: 2 }),
  insurance: numeric("insurance", { precision: 12, scale: 2 }),
  dealerPrincipalComp: numeric("dealer_principal_comp", { precision: 12, scale: 2 }),
  otherOpEx: numeric("other_op_ex", { precision: 12, scale: 2 }),
  usedRetailRevenue: numeric("used_retail_revenue", { precision: 14, scale: 2 }),
  wholesaleRevenue: numeric("wholesale_revenue", { precision: 14, scale: 2 }),
  fiRevenue: numeric("fi_revenue", { precision: 14, scale: 2 }),
  servicePartsRevenue: numeric("service_parts_revenue", { precision: 14, scale: 2 }),
  floorplanInterest: numeric("floorplan_interest", { precision: 12, scale: 2 }),
  floorplanFees: numeric("floorplan_fees", { precision: 12, scale: 2 }),
  reconCosts: numeric("recon_costs", { precision: 12, scale: 2 }),
  transportCosts: numeric("transport_costs", { precision: 12, scale: 2 }),
  totalAssets: numeric("total_assets", { precision: 14, scale: 2 }),
  totalLiabilities: numeric("total_liabilities", { precision: 14, scale: 2 }),
  cashAndEquivalents: numeric("cash_and_equivalents", { precision: 14, scale: 2 }),
  accountsReceivable: numeric("accounts_receivable", { precision: 14, scale: 2 }),
  inventoryValue: numeric("inventory_value", { precision: 14, scale: 2 }),
  prepaidExpenses: numeric("prepaid_expenses", { precision: 14, scale: 2 }),
  otherCurrentAssets: numeric("other_current_assets", { precision: 14, scale: 2 }),
  equipmentValue: numeric("equipment_value", { precision: 14, scale: 2 }),
  realPropertyValue: numeric("real_property_value", { precision: 14, scale: 2 }),
  otherLongTermAssets: numeric("other_long_term_assets", { precision: 14, scale: 2 }),
  otherAssets: numeric("other_assets", { precision: 14, scale: 2 }),
  floorplanPayable: numeric("floorplan_payable", { precision: 14, scale: 2 }),
  accountsPayable: numeric("accounts_payable", { precision: 14, scale: 2 }),
  accruedExpenses: numeric("accrued_expenses", { precision: 14, scale: 2 }),
  otherCurrentLiabilities: numeric("other_current_liabilities", { precision: 14, scale: 2 }),
  longTermDebt: numeric("long_term_debt", { precision: 14, scale: 2 }),
  notesPayable: numeric("notes_payable", { precision: 14, scale: 2 }),
  contributedCapital: numeric("contributed_capital", { precision: 14, scale: 2 }),
  retainedEarnings: numeric("retained_earnings", { precision: 14, scale: 2 }),
  newVehicleRevenue: numeric("new_vehicle_revenue", { precision: 14, scale: 2 }),
  newVehicleCogs: numeric("new_vehicle_cogs", { precision: 14, scale: 2 }),
  docFeesCollected: numeric("doc_fees_collected", { precision: 12, scale: 2 }),
  bhphPaymentIncome: numeric("bhph_payment_income", { precision: 12, scale: 2 }),
  bhphReposCosts: numeric("bhph_repos_costs", { precision: 12, scale: 2 }),
  bhphReserveIncome: numeric("bhph_reserve_income", { precision: 12, scale: 2 }),
  packPerUnit: numeric("pack_per_unit", { precision: 10, scale: 2 }),
  auctionFees: numeric("auction_fees", { precision: 12, scale: 2 }),
  dmsFees: numeric("dms_fees", { precision: 12, scale: 2 }),
  retailInventoryValue: numeric("retail_inventory_value", { precision: 14, scale: 2 }),
  wholesaleInventoryValue: numeric("wholesale_inventory_value", { precision: 14, scale: 2 }),
  citAsset: numeric("cit_asset", { precision: 14, scale: 2 }),
  holdbackReceivable: numeric("holdback_receivable", { precision: 14, scale: 2 }),
  fiReservesReceivable: numeric("fi_reserves_receivable", { precision: 14, scale: 2 }),
  realEstateLoanPayable: numeric("real_estate_loan_payable", { precision: 14, scale: 2 }),
  equipmentLoanPayable: numeric("equipment_loan_payable", { precision: 14, scale: 2 }),
  customFieldsJson: text("custom_fields_json"),
  floorplanByLenderJson: text("floorplan_by_lender_json"),
  ratiosJson: text("ratios_json"),
  status: text("status").notNull().default("completed"),
  cpaPrepared: boolean("cpa_prepared").default(false),
  shortTermNotesPayable: numeric("short_term_notes_payable", { precision: 14, scale: 2 }),
  currentPortionLongTermDebt: numeric("current_portion_long_term_debt", { precision: 14, scale: 2 }),
  notesReceivableShortTerm: numeric("notes_receivable_short_term", { precision: 14, scale: 2 }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertFinancialStatementSchema = createInsertSchema(financialStatements).omit({ id: true, createdAt: true });
export type InsertFinancialStatement = z.infer<typeof insertFinancialStatementSchema>;
export type FinancialStatement = typeof financialStatements.$inferSelect;

export function isValidFinancialStatementIsoDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function isValidFinancialStatementYear(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 1900 && Number(value) <= 2200;
}

export function deriveFinancialStatementPeriodSemantics(
  statementType: "pl" | "balance_sheet",
  periodStart: string,
  periodEnd: string,
  asOfDate: string | null | undefined,
  periodYear: number | null | undefined,
): { periodPrecision: "exact" | "year_only" | "freeform_unparsed"; asOfDate: string | null; periodYear: number | null } {
  const validAsOfDate = isValidFinancialStatementIsoDate(asOfDate) ? asOfDate : null;
  const validPeriodYear = isValidFinancialStatementYear(periodYear) ? periodYear : null;
  if (statementType === "balance_sheet") {
    if (validAsOfDate) return { periodPrecision: "exact", asOfDate: validAsOfDate, periodYear: null };
    if (validPeriodYear != null) return { periodPrecision: "year_only", asOfDate: null, periodYear: validPeriodYear };
    return { periodPrecision: "freeform_unparsed", asOfDate: null, periodYear: null };
  }
  const exactRange = isValidFinancialStatementIsoDate(periodStart)
    && isValidFinancialStatementIsoDate(periodEnd)
    && periodStart <= periodEnd;
  return {
    periodPrecision: exactRange ? "exact" : "freeform_unparsed",
    asOfDate: null,
    periodYear: null,
  };
}

export function deriveUploadedBalanceSheetPeriod(extractedTaxYear: unknown): {
  periodStart: string;
  periodEnd: string;
  sourceType: "uploaded_document";
  periodPrecision: "year_only" | "unknown";
  asOfDate: null;
  periodYear: number | null;
} {
  const rawYear = typeof extractedTaxYear === "number" || typeof extractedTaxYear === "string"
    ? String(extractedTaxYear).trim()
    : "";
  const year = /^\d{4}$/.test(rawYear) && isValidFinancialStatementYear(Number(rawYear))
    ? Number(rawYear)
    : null;
  const compatibleYearLabel = year == null ? "" : String(year);
  return {
    periodStart: compatibleYearLabel,
    periodEnd: compatibleYearLabel,
    sourceType: "uploaded_document",
    periodPrecision: year == null ? "unknown" : "year_only",
    asOfDate: null,
    periodYear: year,
  };
}

// ─── Activity Events ──────────────────────────────────────────────────────────
// Tracks dealer behavior: page visits, tab changes, uploads, AI usage, etc.

export const activityEvents = pgTable("activity_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  eventType: text("event_type").notNull(), // 'page_view' | 'tab_change' | 'document_upload' | 'ai_open' | 'lender_add'
  page: text("page"),
  metadata: text("metadata"), // JSON string for extra context
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertActivityEventSchema = createInsertSchema(activityEvents).omit({ id: true, createdAt: true });
export type InsertActivityEvent = z.infer<typeof insertActivityEventSchema>;
export type ActivityEvent = typeof activityEvents.$inferSelect;

// ─── Commission Events ────────────────────────────────────────────────────────
// Tracks per-dealer per-period commission entries for residual calculations.

export const commissionEvents = pgTable("commission_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  salespersonId: varchar("salesperson_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  period: text("period").notNull(), // 'YYYY-MM' format
  amountEarned: numeric("amount_earned", { precision: 10, scale: 2 }).notNull(),
  monthsElapsed: integer("months_elapsed").notNull().default(1),
  commissionProtected: boolean("commission_protected").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCommissionEventSchema = createInsertSchema(commissionEvents).omit({ id: true, createdAt: true });
export type InsertCommissionEvent = z.infer<typeof insertCommissionEventSchema>;
export type CommissionEvent = typeof commissionEvents.$inferSelect;

// ─── Commission Payouts ───────────────────────────────────────────────────────
// Records confirmed payout disbursements per salesperson per period.

export const commissionPayouts = pgTable("commission_payouts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  salespersonId: varchar("salesperson_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  period: text("period").notNull(), // 'YYYY-MM' format
  amountPaid: numeric("amount_paid", { precision: 10, scale: 2 }),
  confirmedByUserId: varchar("confirmed_by_user_id").references(() => users.id, { onDelete: "set null" }),
  note: text("note"),
  paidAt: timestamp("paid_at").defaultNow(),
  paidBy: varchar("paid_by").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  // Mercury ACH payout fields
  mercuryTransactionId: text("mercury_transaction_id"),
  mercuryStatus: text("mercury_status"), // 'Initiated' | 'Settled' | 'Failed' | null (manual)
  payoutRunId: varchar("payout_run_id"), // FK to payout_runs — set after table is defined
  mercuryError: text("mercury_error"), // error message if failed
  acknowledgedAt: timestamp("acknowledged_at"), // set when admin acknowledges a failure
  acknowledgedNote: text("acknowledged_note"), // optional note left by admin when acknowledging
  acknowledgedByUserId: varchar("acknowledged_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }), // admin who acknowledged
  lastAcknowledgedByUserId: varchar("last_acknowledged_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }), // preserves prior acknowledger when un-acknowledged
}, (t) => ({
  uniqueSpPeriod: unique("commission_payouts_sp_period").on(t.salespersonId, t.period),
}));

export const insertCommissionPayoutSchema = createInsertSchema(commissionPayouts).omit({ id: true, createdAt: true });
export type InsertCommissionPayout = z.infer<typeof insertCommissionPayoutSchema>;
export type CommissionPayout = typeof commissionPayouts.$inferSelect;

// ─── Commission Payout Ack Events (full audit trail) ─────────────────────────
// Records every acknowledge and un-acknowledge action so the full history is
// preserved across multiple review cycles.

export const commissionPayoutAckEvents = pgTable("commission_payout_ack_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  payoutId: varchar("payout_id").notNull().references(() => commissionPayouts.id, { onDelete: "cascade" }),
  actorUserId: varchar("actor_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  actionType: text("action_type").notNull(), // 'acknowledged' | 'unacknowledged'
  note: text("note"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type CommissionPayoutAckEvent = typeof commissionPayoutAckEvents.$inferSelect;

// ─── Platform Settings (global key/value) ────────────────────────────────────

export const platformSettings = pgTable("platform_settings", {
  key: text("key").primaryKey(),
  value: text("value").notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type PlatformSetting = typeof platformSettings.$inferSelect;

// ─── Pricing Overrides (dealer or shared hierarchy/platform custom rates) ────

export const pricingOverrides = pgTable("pricing_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Null identifies an existing non-dealer hierarchy/platform rule; dealer
  // endpoints must still supply an authenticated/authorized dealer ID.
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).unique(),
  basePriceOverride: numeric("base_price_override", { precision: 10, scale: 2 }),
  seatPriceOverride: numeric("seat_price_override", { precision: 10, scale: 2 }),
  label: text("label"), // e.g. "Grandfathered rate"
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  // Explicit: billingCodes.ts upserts ON CONFLICT (user_id). The column-level
  // .unique() above is NOT visible through getTableConfig() when chained after
  // .references(), so the schema-drift check needs this declared index
  // (created live by migration 159).
  uniqueIndex("pricing_overrides_user_id_unique").on(table.userId),
]);

export const insertPricingOverrideSchema = createInsertSchema(pricingOverrides).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPricingOverride = z.infer<typeof insertPricingOverrideSchema>;
export type PricingOverride = typeof pricingOverrides.$inferSelect;

// ─── Promo Campaigns (global discount campaigns) ──────────────────────────────

export const promoCampaigns = pgTable("promo_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  discountType: text("discount_type").notNull(), // 'percent' | 'flat'
  discountValue: numeric("discount_value", { precision: 10, scale: 2 }).notNull(),
  startsAt: timestamp("starts_at").notNull(),
  endsAt: timestamp("ends_at").notNull(),
  newSignupsOnly: boolean("new_signups_only").notNull().default(true),
  isActive: boolean("is_active").notNull().default(true),
  stripeCouponId: text("stripe_coupon_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPromoCampaignSchema = createInsertSchema(promoCampaigns).omit({ id: true, createdAt: true });
export type InsertPromoCampaign = z.infer<typeof insertPromoCampaignSchema>;
export type PromoCampaign = typeof promoCampaigns.$inferSelect;

// ─── Dealer Discounts (per-dealer retention discounts) ────────────────────────

export const dealerDiscounts = pgTable("dealer_discounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  discountType: text("discount_type").notNull(), // 'percent' | 'flat'
  discountValue: numeric("discount_value", { precision: 10, scale: 2 }).notNull(),
  durationMonths: integer("duration_months").notNull(),
  stripeCouponId: text("stripe_coupon_id"),
  expiresAt: timestamp("expires_at"),
  isActive: boolean("is_active").notNull().default(true),
  note: text("note"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDealerDiscountSchema = createInsertSchema(dealerDiscounts).omit({ id: true, createdAt: true });
export type InsertDealerDiscount = z.infer<typeof insertDealerDiscountSchema>;
export type DealerDiscount = typeof dealerDiscounts.$inferSelect;

// ─── Discount Codes (trial / discount / fixed-price codes) ────────────────────

export const discountCodes = pgTable("discount_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull().unique(),
  mode: text("mode").notNull(), // 'trial' | 'discount' | 'fixed_price'
  // discount mode
  discountType: text("discount_type"), // 'percent' | 'flat'
  discountValue: numeric("discount_value", { precision: 10, scale: 2 }),
  stripeCouponId: text("stripe_coupon_id"),
  // fixed_price mode
  fixedPriceAmount: numeric("fixed_price_amount", { precision: 10, scale: 2 }),
  // trial mode
  trialDays: integer("trial_days"),
  // discount & fixed_price: how long the adjusted rate lasts (null = ongoing)
  durationMonths: integer("duration_months"),
  maxRedemptions: integer("max_redemptions"),
  redemptionCount: integer("redemption_count").notNull().default(0),
  expiresAt: timestamp("expires_at"),
  isActive: boolean("is_active").notNull().default(true),
  note: text("note"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDiscountCodeSchema = createInsertSchema(discountCodes).omit({ id: true, redemptionCount: true, createdAt: true });
export type InsertDiscountCode = z.infer<typeof insertDiscountCodeSchema>;
export type DiscountCode = typeof discountCodes.$inferSelect;

export const codeRedemptions = pgTable("code_redemptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  codeId: varchar("code_id").notNull().references(() => discountCodes.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  // redeemedAt = the historical effective date for admin-backdated rows; actual wall-clock time for self-service
  redeemedAt: timestamp("redeemed_at").defaultNow().notNull(),
  // recordedAt = actual wall-clock insert time (not null, default now())
  recordedAt: timestamp("recorded_at").defaultNow().notNull(),
  // fixed_price with a duration: when the override should revert to standard rate
  expiresAt: timestamp("expires_at"),
  revertedAt: timestamp("reverted_at"),
  // Audit / admin-assignment fields
  assignmentSource: text("assignment_source").notNull().default("self_service"),  // 'self_service' | 'admin_assigned'
  assignedByUserId: varchar("assigned_by_user_id").references((): AnyPgColumn => users.id, { onDelete: "set null" }),
  assignmentReason: text("assignment_reason"),        // required for admin assignments
  applicationStatus: text("application_status").notNull().default("applied"), // 'pending' | 'applied' | 'reverted' | 'expired_no_change'
  appliedSubscriptionId: text("applied_subscription_id"),
  appliedStripeCouponId: text("applied_stripe_coupon_id"),
  previousStripeCouponIds: text("previous_stripe_coupon_ids").array(),
  estimatedAdjustmentCents: integer("estimated_adjustment_cents"),
}, (t) => [
  uniqueIndex("code_redemptions_code_user_active_unique")
    .on(t.codeId, t.userId)
    .where(sql`${t.revertedAt} IS NULL`),
]);

export const insertCodeRedemptionSchema = createInsertSchema(codeRedemptions).omit({ id: true, redeemedAt: true });
export type InsertCodeRedemption = z.infer<typeof insertCodeRedemptionSchema>;
export type CodeRedemption = typeof codeRedemptions.$inferSelect;

// ─── Dealership Members (team seats) ─────────────────────────────────────────

export const dealershipMembers = pgTable("dealership_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ownerUserId: varchar("owner_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  memberUserId: varchar("member_user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  role: text("role").notNull().default("viewer"), // 'viewer' | 'editor'
  invitedEmail: text("invited_email").notNull(),
  status: text("status").notNull().default("pending"), // 'pending' | 'active'
  financialAccess: boolean("financial_access").notNull().default(false),
  lenderAccess: boolean("lender_access").notNull().default(false),
  inventoryAccess: boolean("inventory_access").notNull().default(false),
  analyticsAccess: boolean("analytics_access").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDealershipMemberSchema = createInsertSchema(dealershipMembers).omit({ id: true, createdAt: true });
export type InsertDealershipMember = z.infer<typeof insertDealershipMemberSchema>;
export type DealershipMember = typeof dealershipMembers.$inferSelect;

// ─── Salesperson Roles (permission sets for external sales reps) ──────────────
// Mirrors platform_roles but for salesperson-tier users, not internal staff.
// Permission keys (dashboard permissions):
//   dashboard_salesperson  — standard rep view (dealer list, own KPIs, own commissions)
//   dashboard_team         — Sales Leader team performance view, team member KPIs, team commission track
//   admin_salespeople      — view/manage Salespeople tab in admin
//   admin_sales_teams      — view/manage Sales Teams tab in admin
//   admin_company_performance — view company-wide performance and leaderboard tab
//   admin_commissions      — view/action commissions/payout tab

export const salespersonRoles = pgTable("salesperson_roles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  permissions: text("permissions").notNull().default("{}"), // JSON map of permission keys to booleans
  isSystemDefault: boolean("is_system_default").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSalespersonRoleSchema = createInsertSchema(salespersonRoles).omit({ id: true, createdAt: true });
export type InsertSalespersonRole = z.infer<typeof insertSalespersonRoleSchema>;
export type SalespersonRole = typeof salespersonRoles.$inferSelect;

// ─── Platform Roles (internal admin staff roles) ──────────────────────────────

export const platformRoles = pgTable("platform_roles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  permissions: text("permissions").notNull().default("{}"), // JSON map of permission keys to booleans
  isSystemDefault: boolean("is_system_default").default(false), // built-in templates cannot be deleted
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPlatformRoleSchema = createInsertSchema(platformRoles).omit({ id: true, createdAt: true });
export type InsertPlatformRole = z.infer<typeof insertPlatformRoleSchema>;
export type PlatformRole = typeof platformRoles.$inferSelect;

// ─── Platform Staff (user -> role mapping) ────────────────────────────────────

export const platformStaff = pgTable("platform_staff", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  roleId: varchar("role_id").references(() => platformRoles.id, { onDelete: "set null" }), // nullable: template reference only
  permissions: text("permissions").notNull().default("{}"), // person-specific effective permissions JSON
  contractType: text("contract_type").default("employee"), // 'employee' | '1099'
  // 1099 Onboarding Checklist
  contractSignedAt: timestamp("contract_signed_at"),
  taxFormCollectedAt: timestamp("tax_form_collected_at"),
  ndaSignedAt: timestamp("nda_signed_at"),
  accessGrantedAt: timestamp("access_granted_at"),
  firstLoginConfirmed: boolean("first_login_confirmed").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPlatformStaffSchema = createInsertSchema(platformStaff).omit({ id: true, createdAt: true });
export type InsertPlatformStaff = z.infer<typeof insertPlatformStaffSchema>;
export type PlatformStaff = typeof platformStaff.$inferSelect;

// ─── Admin Messages (dealer messaging system) ─────────────────────────────────

export const adminMessages = pgTable("admin_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  subject: text("subject").notNull(),
  body: text("body").notNull(),
  deliveryMethod: text("delivery_method").notNull(), // 'in_app' | 'email' | 'both'
  recipientType: text("recipient_type").notNull(), // 'single' | 'multi' | 'all'
  recipientIds: text("recipient_ids").notNull().default("[]"), // JSON array of user IDs
  recipientCount: integer("recipient_count").notNull().default(0),
  sentByUserId: varchar("sent_by_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAdminMessageSchema = createInsertSchema(adminMessages).omit({ id: true, createdAt: true });
export type InsertAdminMessage = z.infer<typeof insertAdminMessageSchema>;
export type AdminMessage = typeof adminMessages.$inferSelect;

// ─── Dealer Notifications (in-app messages) ───────────────────────────────────

export const dealerNotifications = pgTable("dealer_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  adminMessageId: varchar("admin_message_id").references(() => adminMessages.id, { onDelete: "cascade" }),
  subject: text("subject").notNull(),
  body: text("body").notNull(),
  isRead: boolean("is_read").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDealerNotificationSchema = createInsertSchema(dealerNotifications).omit({ id: true, createdAt: true });
export type InsertDealerNotification = z.infer<typeof insertDealerNotificationSchema>;
export type DealerNotification = typeof dealerNotifications.$inferSelect;

// ─── Dealer Feedback (AI-analyzed feedback submissions) ───────────────────────

export const dealerFeedback = pgTable("dealer_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  description: text("description").notNull(),
  screenshotUrl: text("screenshot_url"),
  pageUrl: text("page_url"),
  aiCategory: text("ai_category"), // 'Bug' | 'Feature Request' | 'Improvement' | 'Praise'
  aiSummary: text("ai_summary"),
  aiRecommendation: text("ai_recommendation"),
  aiInterpretation: text("ai_interpretation"),
  aiImpactScore: integer("ai_impact_score"), // 1-10
  aiBuildComplexity: text("ai_build_complexity"), // 'XS' | 'S' | 'M' | 'L' | 'XL'
  status: text("status").notNull().default("New"), // 'New' | 'In Review' | 'Planned' | 'Shipped' | 'Won\'t Fix'
  internalNotes: text("internal_notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDealerFeedbackSchema = createInsertSchema(dealerFeedback).omit({ id: true, createdAt: true });
export type InsertDealerFeedback = z.infer<typeof insertDealerFeedbackSchema>;
export type DealerFeedback = typeof dealerFeedback.$inferSelect;

// ─── Vendor Refresh Log ───────────────────────────────────────────────────────

export const vendorRefreshLog = pgTable("vendor_refresh_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  triggeredAt: timestamp("triggered_at").notNull().defaultNow(),
  triggeredBy: text("triggered_by").notNull().default("system"),
  vendorsAdded: integer("vendors_added").notNull().default(0),
  vendorsUpdated: integer("vendors_updated").notNull().default(0),
  method: text("method").notNull().default("ai_seed"), // 'ai_seed' | 'manual'
  status: text("status").notNull().default("success"), // 'success' | 'error'
  errorMessage: text("error_message"),
});

export const insertVendorRefreshLogSchema = createInsertSchema(vendorRefreshLog).omit({ id: true, triggeredAt: true });
export type InsertVendorRefreshLog = z.infer<typeof insertVendorRefreshLogSchema>;
export type VendorRefreshLog = typeof vendorRefreshLog.$inferSelect;

// ─── Vendor Interaction Events ────────────────────────────────────────────────

export const vendorInteractionEvents = pgTable("vendor_interaction_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  eventType: text("event_type").notNull(), // 'directory_view' | 'vendor_filter' | 'vendor_link_click'
  vendorId: varchar("vendor_id").references(() => vendors.id, { onDelete: "set null" }),
  metadata: text("metadata"), // JSON for extra context
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertVendorInteractionEventSchema = createInsertSchema(vendorInteractionEvents).omit({ id: true, createdAt: true });
export type InsertVendorInteractionEvent = z.infer<typeof insertVendorInteractionEventSchema>;
export type VendorInteractionEvent = typeof vendorInteractionEvents.$inferSelect;

// ─── Sales Promo Campaigns (salesperson broadcast campaigns) ──────────────────

export const salesPromoCampaigns = pgTable("sales_promo_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  messageBody: text("message_body").notNull(),
  startDate: text("start_date").notNull(), // YYYY-MM-DD
  endDate: text("end_date").notNull(),     // YYYY-MM-DD
  dealerDiscountType: text("dealer_discount_type").notNull(), // 'percent' | 'flat'
  dealerDiscountValue: numeric("dealer_discount_value", { precision: 10, scale: 2 }).notNull(),
  protectCommission: boolean("protect_commission").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  createdByUserId: varchar("created_by_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSalesPromoCampaignSchema = createInsertSchema(salesPromoCampaigns).omit({ id: true, createdAt: true });
export type InsertSalesPromoCampaign = z.infer<typeof insertSalesPromoCampaignSchema>;
export type SalesPromoCampaign = typeof salesPromoCampaigns.$inferSelect;

// ─── Sales Promo Broadcasts (sent broadcast records) ─────────────────────────

export const salesPromoBroadcasts = pgTable("sales_promo_broadcasts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull().references(() => salesPromoCampaigns.id, { onDelete: "cascade" }),
  channel: text("channel").notNull(), // 'email' | 'sms' | 'both'
  recipientType: text("recipient_type").notNull(), // 'all' | 'region' | 'individuals'
  regionFilter: text("region_filter"), // region name if recipientType === 'region'
  individualIds: text("individual_ids").notNull().default("[]"), // JSON array of salesperson IDs
  sentAt: timestamp("sent_at").defaultNow(),
  sentByUserId: varchar("sent_by_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  recipientCount: integer("recipient_count").notNull().default(0),
  skipCount: integer("skip_count").notNull().default(0), // reps skipped (no phone/email)
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSalesPromoBroadcastSchema = createInsertSchema(salesPromoBroadcasts).omit({ id: true, createdAt: true });
export type InsertSalesPromoBroadcast = z.infer<typeof insertSalesPromoBroadcastSchema>;
export type SalesPromoBroadcast = typeof salesPromoBroadcasts.$inferSelect;

// ─── Finn: AI Assistant Preferences ──────────────────────────────────────────

export const finnPreferences = pgTable("finn_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  proactiveMode: boolean("proactive_mode").notNull().default(true), // true = proactive, false = on-demand
  reminderChannel: text("reminder_channel").notNull().default("in_app"), // 'in_app' | 'email'
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertFinnPreferencesSchema = createInsertSchema(finnPreferences).omit({ id: true, updatedAt: true });
export type InsertFinnPreferences = z.infer<typeof insertFinnPreferencesSchema>;
export type FinnPreferences = typeof finnPreferences.$inferSelect;

// ─── Finn: Reminders ──────────────────────────────────────────────────────────

export const finnReminders = pgTable("finn_reminders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  message: text("message").notNull(),
  triggerType: text("trigger_type").notNull(), // 'bank_statement' | 'floorplan_statement' | 'aging' | 'general'
  channel: text("channel").notNull().default("in_app"), // 'in_app' | 'email'
  scheduledAt: timestamp("scheduled_at").notNull(),
  delivered: boolean("delivered").notNull().default(false),
  deliveredAt: timestamp("delivered_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertFinnReminderSchema = createInsertSchema(finnReminders).omit({ id: true, createdAt: true });
export type InsertFinnReminder = z.infer<typeof insertFinnReminderSchema>;
export type FinnReminder = typeof finnReminders.$inferSelect;

// ─── Finn: AI-Generated Alerts ────────────────────────────────────────────────

export const finnAlerts = pgTable("finn_alerts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  alertType: text("alert_type").notNull(), // 'bank_statement_processed' | 'floorplan_analyzed' | 'aging_threshold' | 'vendor_contract'
  title: text("title").notNull(),
  summary: text("summary").notNull(),
  actionPrompt: text("action_prompt"), // e.g. "Ask Finn about it" or "Review now"
  actionLink: text("action_link"), // optional page link
  isRead: boolean("is_read").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  userIdIdx: index("idx_finn_alerts_user_id").on(t.userId),
}));

export const insertFinnAlertSchema = createInsertSchema(finnAlerts).omit({ id: true, createdAt: true });
export type InsertFinnAlert = z.infer<typeof insertFinnAlertSchema>;
export type FinnAlert = typeof finnAlerts.$inferSelect;

// ─── Territories ─────────────────────────────────────────────────────────────

export const territories = pgTable("territories", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  states: text("states").array().notNull().default(sql`'{}'`),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertTerritorySchema = createInsertSchema(territories).omit({ id: true, createdAt: true });
export type InsertTerritory = z.infer<typeof insertTerritorySchema>;
export type Territory = typeof territories.$inferSelect;

// ─── Markets ──────────────────────────────────────────────────────────────────

export const markets = pgTable("markets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  territoryId: varchar("territory_id").notNull().references(() => territories.id, { onDelete: "cascade" }),
  zipCodes: text("zip_codes").array().notNull().default(sql`'{}'`),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertMarketSchema = createInsertSchema(markets).omit({ id: true, createdAt: true });
export type InsertMarket = z.infer<typeof insertMarketSchema>;
export type Market = typeof markets.$inferSelect;
export type MarketWithTerritory = Market & { territoryName: string };

// ─── Dealer Status Events (audit log) ────────────────────────────────────────

export const dealerStatusEvents = pgTable("dealer_status_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  changedByUserId: varchar("changed_by_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  fromStatus: text("from_status").notNull(),
  toStatus: text("to_status").notNull(),
  reason: text("reason").notNull(),
  note: text("note"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDealerStatusEventSchema = createInsertSchema(dealerStatusEvents).omit({ id: true, createdAt: true });
export type InsertDealerStatusEvent = z.infer<typeof insertDealerStatusEventSchema>;
export type DealerStatusEvent = typeof dealerStatusEvents.$inferSelect;

// ─── Salesperson Audit Events ─────────────────────────────────────────────────

export const salespersonAuditEvents = pgTable("salesperson_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  repId: varchar("rep_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  changedBy: varchar("changed_by").notNull().references(() => users.id, { onDelete: "cascade" }),
  eventType: text("event_type").notNull(), // 'status_change' | 'commission_change' | 'territory_change' | 'goal_change'
  fieldChanged: text("field_changed"),
  fromValue: text("from_value"),
  toValue: text("to_value"),
  reason: text("reason"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  repIdIdx: index("idx_salesperson_audit_events_rep_id").on(t.repId),
}));

export const insertSalespersonAuditEventSchema = createInsertSchema(salespersonAuditEvents).omit({ id: true, createdAt: true });
export type InsertSalespersonAuditEvent = z.infer<typeof insertSalespersonAuditEventSchema>;
export type SalespersonAuditEvent = typeof salespersonAuditEvents.$inferSelect;

// ─── DEAN Exchange Audit Events ───────────────────────────────────────────────

export const deanBetaExchangeMetrics = pgTable("dean_beta_exchange_metrics", {
  exchangeId: varchar("exchange_id").primaryKey(),
  dealerId: varchar("dealer_id").references(() => users.id, { onDelete: "set null" }),
  betaEnabled: boolean("beta_enabled").notNull().default(false),
  status: text("status").notNull(),
  providerStatuses: jsonb("provider_statuses").notNull().default([]),
  blocked: boolean("blocked").notNull().default(false),
  fallback: boolean("fallback").notNull().default(false),
  sentencesRemoved: integer("sentences_removed").notNull().default(0),
  citationMisbinds: integer("citation_misbinds").notNull().default(0),
  freshnessRetries: integer("freshness_retries").notNull().default(0),
  roleDenials: integer("role_denials").notNull().default(0),
  runtimeTools: integer("runtime_tools").notNull().default(0),
  latencyMs: integer("latency_ms").notNull(),
  costUsd: doublePrecision("cost_usd"),
  costIncomplete: boolean("cost_incomplete").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [index("idx_dean_beta_metrics_created").on(t.createdAt)]);

export const deanBetaAlerts = pgTable("dean_beta_alerts", {
  day: date("day").notNull(),
  kind: text("kind").notNull(),
  active: boolean("active").notNull(),
  value: doublePrecision("value").notNull(),
  threshold: doublePrecision("threshold").notNull(),
  notify: boolean("notify").notNull().default(false),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, t => [uniqueIndex("dean_beta_alerts_day_kind").on(t.day, t.kind)]);

export const deanExchangeAuditEvents = pgTable("dean_exchange_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  exchangeId: varchar("exchange_id").notNull().unique("dean_exchange_audit_events_exchange_id_key"),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  conversationId: integer("conversation_id").notNull(),
  requestCategory: text("request_category").notNull(),
  lifecycleStage: text("lifecycle_stage"),
  contextDegraded: boolean("context_degraded").notNull().default(false),
  pageSummary: boolean("page_summary").notNull().default(false),
  analytical: boolean("analytical").notNull().default(false),
  verifierDecision: text("verifier_decision").notNull(),
  verifierIssueCount: integer("verifier_issue_count").notNull().default(0),
  groundingFallbackReason: text("grounding_fallback_reason"),
  publicationPath: text("publication_path").notNull(),
  usedAuthorizedDocument: boolean("used_authorized_document").notNull().default(false),
  usedWebEvidence: boolean("used_web_evidence").notNull().default(false),
  evidenceAvailabilityJson: jsonb("evidence_availability_json").notNull().default({}),
  stageTimingsMs: jsonb("stage_timings_ms").notNull().default({}),
  candidateCompletedAt: timestamp("candidate_completed_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  dealerCreatedIdx: index("idx_dean_exchange_audit_events_dealer_created").on(t.dealerId, t.createdAt),
}));

// ─── AI Usage Logs ────────────────────────────────────────────────────────────

export const aiUsageLogs = pgTable("ai_usage_logs", {
  id: serial("id").primaryKey(),
  provider: text("provider").notNull(), // 'anthropic' | 'openai'
  model: text("model").notNull(),
  feature: text("feature").notNull(), // 'doc_review' | 'doc_verification' | 'dean_chat' | 'deans_son_chat' | 'doc_summarization' | 'admin_insights' | 'image_gen'
  persona: text("persona"), // nullable: 'dean' | 'deans_son'
  dealerId: varchar("dealer_id").references(() => users.id, { onDelete: "set null" }),
  inputTokens: integer("input_tokens").notNull().default(0),
  outputTokens: integer("output_tokens").notNull().default(0),
  estimatedCostCents: integer("estimated_cost_cents").notNull().default(0),
  confidenceScore: numeric("confidence_score", { precision: 5, scale: 4 }), // nullable float 0.0000–1.0000
  status: text("status").notNull().default("success"), // 'success' | 'error'
  errorDetail: text("error_detail"), // raw error message/code on failure; null for successful calls
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAiUsageLogSchema = createInsertSchema(aiUsageLogs).omit({ id: true, createdAt: true });
export type InsertAiUsageLog = z.infer<typeof insertAiUsageLogSchema>;
export type AiUsageLog = typeof aiUsageLogs.$inferSelect;

// ─── DEAN Conversation Training Logs ─────────────────────────────────────────
// Opt-in, anonymized conversation pairs for future fine-tuning ("DEAN's son").
// No PII, no dealer-identifiable data — only exchange content + lifecycle stage.

export const deanConversationLogs = pgTable("dean_conversation_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerPrompt: text("dealer_prompt").notNull(),
  deanResponse: text("dean_response").notNull(),
  lifecycleStage: text("lifecycle_stage").notNull(), // 'new' | 'growing' | 'established' | 'at_risk'
  qualitySignal: text("quality_signal").notNull(), // 'thumbs_up' | 'dean_confident'
  sourceMessageId: integer("source_message_id"), // idempotency key — prevents duplicate thumbs-ups for the same message
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => [uniqueIndex("dean_logs_message_quality_uidx").on(t.sourceMessageId, t.qualitySignal)]);

export const insertDeanConversationLogSchema = createInsertSchema(deanConversationLogs).omit({ id: true, createdAt: true });
export type InsertDeanConversationLog = z.infer<typeof insertDeanConversationLogSchema>;
export type DeanConversationLog = typeof deanConversationLogs.$inferSelect;

// Per-dealership explicit opt-in to using DEAN conversations for model training.
// Missing rows are treated as disabled by storage; global platform control
// remains a separate kill switch.
export const deanTrainingConsents = pgTable("dean_training_consents", {
  dealerOwnerId: varchar("dealer_owner_id").primaryKey().references(() => users.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").notNull().default(false),
  changedByUserId: varchar("changed_by_user_id").notNull().references(() => users.id, { onDelete: "restrict" }),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});

export type DeanTrainingConsent = typeof deanTrainingConsents.$inferSelect;

// ─── Dealer Assessments ───────────────────────────────────────────────────────
// Stores the AI-generated consultant assessment for a dealer.
// Regenerated automatically when source data changes.

export const dealerAssessments = pgTable("dealer_assessments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  generatedAt: timestamp("generated_at").notNull().defaultNow(),
  overallConfidence: numeric("overall_confidence", { precision: 5, scale: 2 }),
  payloadJson: text("payload_json"), // structured aggregated data used for generation
  narrativeJson: text("narrative_json"), // AI-generated domain findings JSON
  dataGapsJson: text("data_gaps_json"), // list of missing data items
  dealerScore: integer("dealer_score"), // 0–100 gamified score
  dealerScoreTier: varchar("dealer_score_tier"), // Rookie | Rising | Competitive | Elite
  dealerScoreJson: text("dealer_score_json"), // full score breakdown JSON
  updatedAt: timestamp("updated_at").defaultNow(),
  sourceGeneration: bigint("source_generation", { mode: "number" }),
});

export const insertDealerAssessmentSchema = createInsertSchema(dealerAssessments).omit({ id: true, updatedAt: true });
export type InsertDealerAssessment = z.infer<typeof insertDealerAssessmentSchema>;
export type DealerAssessment = typeof dealerAssessments.$inferSelect;

export const dealerEvidenceGenerations = pgTable("dealer_evidence_generations", {
  dealerId: varchar("dealer_id").primaryKey().references(() => users.id, { onDelete: "cascade" }),
  generation: bigint("generation", { mode: "number" }).notNull().default(0),
  assessmentGeneration: bigint("assessment_generation", { mode: "number" }),
  savingsGeneration: bigint("savings_generation", { mode: "number" }),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// ─── Commission Ledger ────────────────────────────────────────────────────────
// Records per-invoice commission entries created by the invoice.payment_succeeded
// webhook. Each row represents actual money earned, not an estimate.

export const commissionLedger = pgTable("commission_ledger", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  salespersonId: varchar("salesperson_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerUserId: varchar("dealer_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  stripeInvoiceId: text("stripe_invoice_id").notNull(),
  invoiceAmount: numeric("invoice_amount", { precision: 12, scale: 2 }).notNull(), // in dollars
  commissionAmount: numeric("commission_amount", { precision: 10, scale: 2 }).notNull(),
  currency: text("currency").notNull().default("usd"),
  paidAt: timestamp("paid_at"), // null = Pending; non-null = Paid (set by admin)
  entryType: text("entry_type").notNull().default("personal"), // 'personal' | 'team_override'
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  invoiceEntryTypeUnique: unique("commission_ledger_invoice_entry_type_unique").on(t.stripeInvoiceId, t.entryType, t.salespersonId),
}));

export const insertCommissionLedgerSchema = createInsertSchema(commissionLedger).omit({ id: true, createdAt: true });
export type InsertCommissionLedger = z.infer<typeof insertCommissionLedgerSchema>;
export type CommissionLedger = typeof commissionLedger.$inferSelect;

// ─── Performance Snapshots ────────────────────────────────────────────────────
// Monthly snapshot of a salesperson's performance metrics.
// Enables fast multi-period historical queries without recomputing from raw data.

export const performanceSnapshots = pgTable("performance_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  salespersonId: varchar("salesperson_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  periodMonth: text("period_month").notNull(), // 'YYYY-MM'
  dealerCount: integer("dealer_count").notNull().default(0),
  activeDealers: integer("active_dealers").notNull().default(0),
  newDealers: integer("new_dealers").notNull().default(0),
  grossMrr: numeric("gross_mrr", { precision: 12, scale: 2 }).notNull().default("0"),
  commissionEarned: numeric("commission_earned", { precision: 12, scale: 2 }).notNull().default("0"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  uniqueSpPeriod: unique("performance_snapshots_sp_period").on(t.salespersonId, t.periodMonth),
}));

export const insertPerformanceSnapshotSchema = createInsertSchema(performanceSnapshots).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPerformanceSnapshot = z.infer<typeof insertPerformanceSnapshotSchema>;
export type PerformanceSnapshot = typeof performanceSnapshots.$inferSelect;

// ─── Dealer Payment Clearances ────────────────────────────────────────────────
// Tracks Stripe charge settlement/clearing data per dealer subscription payment.
// Populated by the invoice.payment_succeeded webhook event.

export const dealerPaymentClearances = pgTable("dealer_payment_clearances", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  stripeChargeId: text("stripe_charge_id").notNull().unique(),
  paymentDate: text("payment_date").notNull(), // YYYY-MM-DD
  paymentMethodType: text("payment_method_type").notNull(), // 'card' | 'bank_account'
  availableOn: text("available_on"), // YYYY-MM-DD from Stripe balance transaction (nullable)
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertDealerPaymentClearanceSchema = createInsertSchema(dealerPaymentClearances).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDealerPaymentClearance = z.infer<typeof insertDealerPaymentClearanceSchema>;
export type DealerPaymentClearance = typeof dealerPaymentClearances.$inferSelect;

// ─── Admin Edit Requests ──────────────────────────────────────────────────────
// Tracks pending/approved/denied requests from an admin to edit a dealer's data
// while impersonating. Scoped to a single impersonation session; a new record
// is created each time the admin requests edit access.

export const adminEditRequests = pgTable("admin_edit_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  adminUserId: varchar("admin_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerUserId: varchar("dealer_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("pending"), // 'pending' | 'approved' | 'denied' | 'revoked'
  sessionId: varchar("session_id"), // unique per impersonation session; null = legacy
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertAdminEditRequestSchema = createInsertSchema(adminEditRequests).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertAdminEditRequest = z.infer<typeof insertAdminEditRequestSchema>;
export type AdminEditRequest = typeof adminEditRequests.$inferSelect;

// ─── Ghost Session Logs ───────────────────────────────────────────────────────
// Admin-only audit trail of "Ghost Mode" impersonation sessions (traceless,
// hard read-only viewing of a dealer's account). Invisible to dealers — never
// surfaced through any dealer-facing endpoint.

export const ghostSessionLogs = pgTable("ghost_session_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  adminUserId: varchar("admin_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerUserId: varchar("dealer_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sessionId: varchar("session_id").notNull(), // impersonationSessionId of the ghost session
  startedAt: timestamp("started_at").defaultNow(),
  endedAt: timestamp("ended_at"),
});

export const insertGhostSessionLogSchema = createInsertSchema(ghostSessionLogs).omit({ id: true, startedAt: true });
export type InsertGhostSessionLog = z.infer<typeof insertGhostSessionLogSchema>;
export type GhostSessionLog = typeof ghostSessionLogs.$inferSelect;

// ─── Pay Frequency Schedules ──────────────────────────────────────────────────
// Audit log of all default pay-frequency changes with effective dates.
// A record with appliedAt = null and effectiveDate in the future is the
// single "pending" schedule; only one may exist at a time.

export const payFrequencySchedules = pgTable("pay_frequency_schedules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  frequency: text("frequency").notNull(), // 'weekly' | 'biweekly' | 'monthly'
  effectiveDate: text("effective_date").notNull(), // YYYY-MM-DD
  appliedAt: timestamp("applied_at"), // null = pending / not yet applied
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPayFrequencyScheduleSchema = createInsertSchema(payFrequencySchedules).omit({ id: true, createdAt: true });
export type InsertPayFrequencySchedule = z.infer<typeof insertPayFrequencyScheduleSchema>;
export type PayFrequencySchedule = typeof payFrequencySchedules.$inferSelect;

// ─── Contractor Bank Accounts ─────────────────────────────────────────────────
// Stores encrypted ACH bank account info for 1099 contractor salespeople.
// Account numbers are stored encrypted; only the last 4 digits are exposed via API.

export const contractorBankAccounts = pgTable("contractor_bank_accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  salespersonId: varchar("salesperson_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  accountHolderName: text("account_holder_name").notNull(),
  routingNumber: text("routing_number").notNull(), // stored AES-256-GCM encrypted
  accountNumber: text("account_number").notNull(), // stored AES-256-GCM encrypted
  routingLast4: text("routing_last4"), // last 4 digits of plaintext routing number, stored for display
  accountLast4: text("account_last4"), // last 4 digits of plaintext account number, stored for display
  accountType: text("account_type").notNull().default("checking"), // 'checking' | 'savings'
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertContractorBankAccountSchema = createInsertSchema(contractorBankAccounts).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertContractorBankAccount = z.infer<typeof insertContractorBankAccountSchema>;
export type ContractorBankAccount = typeof contractorBankAccounts.$inferSelect;

// ─── Payout Runs ──────────────────────────────────────────────────────────────
// Audit log of each monthly payout job execution.

export const payoutRuns = pgTable("payout_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  period: text("period").notNull(), // 'YYYY-MM' — the commission period being paid
  runAt: timestamp("run_at").defaultNow(),
  triggeredBy: text("triggered_by").notNull().default("scheduler"), // 'scheduler' | 'manual'
  triggeredByUserId: varchar("triggered_by_user_id").references(() => users.id, { onDelete: "set null" }),
  repsPaid: integer("reps_paid").notNull().default(0),
  totalAmountCents: integer("total_amount_cents").notNull().default(0),
  failureCount: integer("failure_count").notNull().default(0),
  skippedCount: integer("skipped_count").notNull().default(0),
  detailsJson: text("details_json"), // JSON array of per-rep results
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPayoutRunSchema = createInsertSchema(payoutRuns).omit({ id: true, createdAt: true });
export type InsertPayoutRun = z.infer<typeof insertPayoutRunSchema>;
export type PayoutRun = typeof payoutRuns.$inferSelect;

// ─── Dealer Data SLA ──────────────────────────────────────────────────────────
// Per-dealer upload schedule commitments for each core data type.
// Dealers self-configure the cadence; the system evaluates breach against it.

export const DATA_SLA_TYPES = ["receivables", "p_and_l", "bank_statement", "balance_sheet", "inventory"] as const;
export type DataSlaType = typeof DATA_SLA_TYPES[number];

export const DATA_SLA_FREQUENCIES = ["weekly", "monthly", "quarterly", "manual"] as const;
export type DataSlaFrequency = typeof DATA_SLA_FREQUENCIES[number];

export const dealerDataSla = pgTable("dealer_data_sla", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dataType: text("data_type").notNull(), // DataSlaType
  frequency: text("frequency").notNull().default("monthly"), // DataSlaFrequency
  dayTarget: integer("day_target"), // day-of-month for monthly/quarterly; day-of-week 0–6 for weekly; null = manual
  isEnabled: boolean("is_enabled").notNull().default(true),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  userTypeUnique: uniqueIndex("idx_dealer_data_sla_user_type").on(t.userId, t.dataType),
}));

export const insertDealerDataSlaSchema = createInsertSchema(dealerDataSla).omit({ id: true, updatedAt: true });
export type InsertDealerDataSla = z.infer<typeof insertDealerDataSlaSchema>;
export type DealerDataSla = typeof dealerDataSla.$inferSelect;

// ─── Saved Tax Returns ────────────────────────────────────────────────────────
// Dealer-saved snapshots of extracted tax return financials, named by the dealer.

export const savedTaxReturns = pgTable("saved_tax_returns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  fileName: text("file_name").notNull(),
  cloudPath: text("cloud_path"),
  snapshotJson: text("snapshot_json").notNull(),
  sourceVersion: text("source_version").notNull().default("1"),
  sourceJobId: varchar("source_job_id"),
  contentHash: text("content_hash"),
  saveRequestKey: varchar("save_request_key", { length: 128 }),
  savedAt: timestamp("saved_at").defaultNow(),
}, (table) => [
  uniqueIndex("saved_tax_returns_dealer_save_request_uq").on(table.dealerId, table.saveRequestKey),
]);

export const insertSavedTaxReturnSchema = createInsertSchema(savedTaxReturns).omit({ id: true, savedAt: true });
export type InsertSavedTaxReturn = z.infer<typeof insertSavedTaxReturnSchema>;
export type SavedTaxReturn = typeof savedTaxReturns.$inferSelect;

// ─── Extraction Memories ─────────────────────────────────────────────────────
// Per-dealer self-learning layer: after every successful floorplan extraction,
// a compact record is saved so future extractions for the same lender can be
// primed with worked examples (few-shot context for Claude).

export const extractionMemories = pgTable("extraction_memories", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerId: varchar("dealer_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  lenderName: text("lender_name").notNull(), // normalized (lowercased)
  documentFingerprint: text("document_fingerprint").notNull(), // hash of column headers + key structural phrases
  exampleInputSummary: text("example_input_summary").notNull(), // compact text excerpt (a few representative rows from markdown)
  exampleOutputJson: text("example_output_json").notNull(), // first 3 vehicle receivables JSON
  extractionStatus: text("extraction_status").notNull(), // 'verified' | 'ai-corrected' | 'unverified'
  unitCount: integer("unit_count").notNull().default(0),
  exampleImageObjectKey: text("example_image_object_key"), // cloud storage key for page-image thumbnail (vision few-shot)
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertExtractionMemorySchema = createInsertSchema(extractionMemories).omit({ id: true, createdAt: true });
export type InsertExtractionMemory = z.infer<typeof insertExtractionMemorySchema>;
export type ExtractionMemory = typeof extractionMemories.$inferSelect;

// ─── Memory Promotions (dev → prod auto-promotion queue + audit) ─────────────
// Every newly saved extraction memory in dev is classified by guardrails:
// "auto" (safe → promoted to prod automatically) or "hold" (queued for owner
// review in the admin "Database Training" section). Each row is BOTH the queue
// entry and the local audit record of what happened (who/what/when/why).
// Status lifecycle:
//   queued         — passed guardrails (or approved), awaiting/retrying prod write
//   pending_review — failed a guardrail; needs owner Approve/Reject
//   promoted       — written to prod (audited in prod config_promotions too)
//   rejected       — owner rejected; archived with reason
export const memoryPromotions = pgTable("memory_promotions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  memoryId: varchar("memory_id"), // dev extraction_memories.id (informational; no FK so memory deletion never breaks audit)
  dealerId: varchar("dealer_id").notNull(), // dev dealer that produced the memory (env-local; never crosses envs)
  lenderName: text("lender_name").notNull(),
  documentFingerprint: text("document_fingerprint").notNull(),
  exampleInputSummary: text("example_input_summary").notNull(),
  exampleOutputJson: text("example_output_json").notNull(),
  extractionStatus: text("extraction_status").notNull(),
  unitCount: integer("unit_count").notNull().default(0),
  classification: text("classification").notNull(), // 'auto' | 'hold'
  status: text("status").notNull().default("queued"), // 'queued' | 'pending_review' | 'promoted' | 'rejected'
  holdReason: text("hold_reason"), // the specific guardrail that failed (null for auto)
  guardrailsJson: jsonb("guardrails_json").$type<{ name: string; passed: boolean; detail?: string }[]>(),
  attempts: integer("attempts").notNull().default(0),
  lastError: text("last_error"),
  decidedBy: text("decided_by"), // 'auto-guardrails' for auto-promotions; admin identity for review decisions
  decisionReason: text("decision_reason"),
  decidedAt: timestamp("decided_at"),
  promotedAt: timestamp("promoted_at"),
  prodAction: text("prod_action"), // 'inserted' | 'updated' | 'unchanged' (what the prod upsert did)
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_memory_promotions_status_created").on(table.status, table.createdAt),
]);

export const insertMemoryPromotionSchema = createInsertSchema(memoryPromotions).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertMemoryPromotion = z.infer<typeof insertMemoryPromotionSchema>;
export type MemoryPromotion = typeof memoryPromotions.$inferSelect;

// ─── Primary Lenders (structured) ────────────────────────────────────────────
export interface PrimaryLender {
  name: string;
  type: "prime" | "subprime" | "mix";
  isPrimary: boolean;
}

export const primaryLenderSchema = z.object({
  name: z.string().min(1),
  type: z.enum(["prime", "subprime", "mix"]),
  isPrimary: z.boolean(),
});

// ─── Dealer Marketplace ───────────────────────────────────────────────────────

export const MARKETPLACE_STATUSES = ["draft", "active", "pending", "sold"] as const;
export type MarketplaceStatus = typeof MARKETPLACE_STATUSES[number];

export const MARKETPLACE_CONDITIONS = ["excellent", "good", "fair", "poor"] as const;
export type MarketplaceCondition = typeof MARKETPLACE_CONDITIONS[number];

export const marketplaceListings = pgTable("marketplace_listings", {
  id: uuid("id").primaryKey().defaultRandom(),
  sellerUserId: text("seller_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sourceInventoryId: text("source_inventory_id").references(() => inventoryItems.id, { onDelete: "set null" }),
  vin: text("vin"),
  year: integer("year"),
  make: text("make"),
  model: text("model"),
  trim: text("trim"),
  bodyStyle: text("body_style"),
  color: text("color"),
  mileage: integer("mileage"),
  askingPrice: numeric("asking_price", { precision: 12, scale: 2 }),
  description: text("description"),
  reconNotes: text("recon_notes"),
  condition: text("condition").default("good"), // MarketplaceCondition
  status: text("status").notNull().default("draft"), // MarketplaceStatus
  sellerZip: text("seller_zip"),
  sellerCity: text("seller_city"),
  sellerState: text("seller_state"),
  sellerLat: doublePrecision("seller_lat"),
  sellerLng: doublePrecision("seller_lng"),
  contactPhone: text("contact_phone"),
  contactEmail: text("contact_email"),
  showPhone: boolean("show_phone").notNull().default(false),
  showEmail: boolean("show_email").notNull().default(false),
  soldChannel: text("sold_channel"), // 'marketplace' | 'other' | null
  buyerUserId: text("buyer_user_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  statusIdx: index("idx_marketplace_listings_status").on(t.status),
  makeIdx: index("idx_marketplace_listings_make").on(t.make),
  modelIdx: index("idx_marketplace_listings_model").on(t.model),
  yearIdx: index("idx_marketplace_listings_year").on(t.year),
  askingPriceIdx: index("idx_marketplace_listings_asking_price").on(t.askingPrice),
}));

export const insertMarketplaceListingSchema = createInsertSchema(marketplaceListings).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertMarketplaceListing = z.infer<typeof insertMarketplaceListingSchema>;
export type MarketplaceListing = typeof marketplaceListings.$inferSelect;

export const marketplaceListingPhotos = pgTable("marketplace_listing_photos", {
  id: uuid("id").primaryKey().defaultRandom(),
  listingId: uuid("listing_id").notNull().references(() => marketplaceListings.id, { onDelete: "cascade" }),
  photoUrl: text("photo_url").notNull(),
  displayOrder: integer("display_order").notNull().default(0),
});

export const insertMarketplaceListingPhotoSchema = createInsertSchema(marketplaceListingPhotos).omit({ id: true });
export type InsertMarketplaceListingPhoto = z.infer<typeof insertMarketplaceListingPhotoSchema>;
export type MarketplaceListingPhoto = typeof marketplaceListingPhotos.$inferSelect;

export type MarketplaceListingWithDetails = MarketplaceListing & {
  photos: MarketplaceListingPhoto[];
  sellerDealershipName: string | null;
  sellerUsername: string;
  daysListed: number;
  sellerAvgRating: number | null;
  sellerReviewCount: number;
  // Contact info — only present when viewer is authenticated and not the seller
  contactPhone?: string | null;
  contactEmail?: string | null;
};

// ─── Marketplace Conversations ────────────────────────────────────────────────
// Tracks buyer↔seller contact threads per listing (prerequisite for reviews).

export const marketplaceConversations = pgTable("marketplace_conversations", {
  id: uuid("id").primaryKey().defaultRandom(),
  listingId: uuid("listing_id").notNull().references(() => marketplaceListings.id, { onDelete: "cascade" }),
  buyerUserId: text("buyer_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sellerUserId: text("seller_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  listingBuyerUnique: unique("marketplace_conversations_listing_buyer_unique").on(t.listingId, t.buyerUserId),
}));

export const insertMarketplaceConversationSchema = createInsertSchema(marketplaceConversations).omit({ id: true, createdAt: true });
export type InsertMarketplaceConversation = z.infer<typeof insertMarketplaceConversationSchema>;
export type MarketplaceConversation = typeof marketplaceConversations.$inferSelect;

// ─── Marketplace Messages ─────────────────────────────────────────────────────
// Individual messages within a marketplace_conversations thread.

export const marketplaceMessages = pgTable("marketplace_messages", {
  id: uuid("id").primaryKey().defaultRandom(),
  conversationId: uuid("conversation_id").notNull().references(() => marketplaceConversations.id, { onDelete: "cascade" }),
  senderUserId: text("sender_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  body: text("body").notNull(),
  sentAt: timestamp("sent_at", { withTimezone: true }).defaultNow(),
  readAt: timestamp("read_at", { withTimezone: true }),
});

export const insertMarketplaceMessageSchema = createInsertSchema(marketplaceMessages).omit({ id: true, sentAt: true, readAt: true });
export type InsertMarketplaceMessage = z.infer<typeof insertMarketplaceMessageSchema>;
export type MarketplaceMessage = typeof marketplaceMessages.$inferSelect;

export type MarketplaceConversationWithDetails = MarketplaceConversation & {
  listingTitle: string;
  listingHeroPhoto: string | null;
  listingStatus: string;
  otherPartyName: string;
  otherPartyUserId: string;
  lastMessageBody: string | null;
  lastMessageAt: Date | null;
  lastMessageSenderUserId: string | null;
  unreadCount: number;
};

// ─── Marketplace Reviews ──────────────────────────────────────────────────────
// One review per listing per reviewer; tied to completed transactions.

export const marketplaceReviews = pgTable("marketplace_reviews", {
  id: uuid("id").primaryKey().defaultRandom(),
  listingId: uuid("listing_id").notNull().references(() => marketplaceListings.id, { onDelete: "cascade" }),
  reviewerUserId: text("reviewer_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sellerUserId: text("seller_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  rating: integer("rating").notNull(),
  reviewText: text("review_text"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  listingReviewerUnique: unique("marketplace_reviews_listing_reviewer_unique").on(t.listingId, t.reviewerUserId),
}));

export const insertMarketplaceReviewSchema = createInsertSchema(marketplaceReviews)
  .omit({ id: true, createdAt: true })
  .extend({ rating: z.number().int().min(1).max(5) });
export type InsertMarketplaceReview = z.infer<typeof insertMarketplaceReviewSchema>;
export type MarketplaceReview = typeof marketplaceReviews.$inferSelect;

export type MarketplaceReviewWithDetails = MarketplaceReview & {
  reviewerUsername: string;
  reviewerDealershipName: string | null;
};

export type DealerReviewsResult = {
  reviews: MarketplaceReviewWithDetails[];
  avgRating: number | null;
  totalCount: number;
};

// ─── Statement Reader Sessions ───────────────────────────────────────────────
// Server-side conversation history for the Statement Reader chatbot.
// One session per (userId, statementId) pair — cleared on "Start over".
export const statementReaderSessions = pgTable("statement_reader_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  statementId: varchar("statement_id").notNull().references(() => parsedStatements.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  userStatementUnique: unique("statement_reader_sessions_user_statement_unique").on(t.userId, t.statementId),
}));

export const insertStatementReaderSessionSchema = createInsertSchema(statementReaderSessions).omit({ id: true, createdAt: true });
export type InsertStatementReaderSession = z.infer<typeof insertStatementReaderSessionSchema>;
export type StatementReaderSession = typeof statementReaderSessions.$inferSelect;

export const statementReaderMessages = pgTable("statement_reader_messages", {
  id: serial("id").primaryKey(),
  sessionId: varchar("session_id").notNull().references(() => statementReaderSessions.id, { onDelete: "cascade" }),
  role: text("role").notNull(), // 'user' | 'assistant'
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertStatementReaderMessageSchema = createInsertSchema(statementReaderMessages).omit({ id: true, createdAt: true });
export type InsertStatementReaderMessage = z.infer<typeof insertStatementReaderMessageSchema>;
export type StatementReaderMessage = typeof statementReaderMessages.$inferSelect;

// ─── Marketplace Profiles ────────────────────────────────────────────────────
// Marketplace-specific public profile, independent of the core dealer_profiles.
export const marketplaceProfiles = pgTable("marketplace_profiles", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  displayName: text("display_name"),
  phone: text("phone"),
  email: text("email"),
  address: text("address"),
  city: text("city"),
  stateCode: text("state_code"),
  bio: text("bio"),
  showPhone: boolean("show_phone").notNull().default(false),
  showEmail: boolean("show_email").notNull().default(false),
  showAddress: boolean("show_address").notNull().default(true),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertMarketplaceProfileSchema = createInsertSchema(marketplaceProfiles).omit({ id: true, updatedAt: true });
export type InsertMarketplaceProfile = z.infer<typeof insertMarketplaceProfileSchema>;
export type MarketplaceProfile = typeof marketplaceProfiles.$inferSelect;

// Fields a dealer MUST fill in before they can use the marketplace. The short
// bio is intentionally excluded (optional). Visibility toggles have defaults.
export const MARKETPLACE_PROFILE_REQUIRED_FIELDS = [
  "displayName",
  "phone",
  "email",
  "address",
  "city",
  "stateCode",
] as const;

// A marketplace profile is "complete" only when every required field is a
// non-empty string. Used to gate all marketplace access (server + client).
export function isMarketplaceProfileComplete(
  profile: Partial<MarketplaceProfile> | null | undefined,
): boolean {
  if (!profile) return false;
  return MARKETPLACE_PROFILE_REQUIRED_FIELDS.every((field) => {
    const value = (profile as Record<string, unknown>)[field];
    return typeof value === "string" && value.trim().length > 0;
  });
}

// ─── Dealer Social Feed ───────────────────────────────────────────────────────
// A Twitter-style network for dealers. Handle = "@" + users.username.
// One shared global feed; access can be rescinded per-user via social_bans.

export const socialPosts = pgTable("social_posts", {
  id: uuid("id").primaryKey().defaultRandom(),
  authorUserId: text("author_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  body: text("body").notNull().default(""),
  mediaUrls: text("media_urls").array().notNull().default(sql`'{}'::text[]`),
  hashtags: text("hashtags").array().notNull().default(sql`'{}'::text[]`),
  mentionedUserIds: text("mentioned_user_ids").array().notNull().default(sql`'{}'::text[]`),
  likeCount: integer("like_count").notNull().default(0),
  dislikeCount: integer("dislike_count").notNull().default(0),
  commentCount: integer("comment_count").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  authorIdx: index("idx_social_posts_author").on(t.authorUserId),
  createdIdx: index("idx_social_posts_created").on(t.createdAt),
}));

export const insertSocialPostSchema = createInsertSchema(socialPosts).omit({
  id: true,
  hashtags: true,
  mentionedUserIds: true,
  likeCount: true,
  dislikeCount: true,
  commentCount: true,
  createdAt: true,
});
export type InsertSocialPost = z.infer<typeof insertSocialPostSchema>;
export type SocialPost = typeof socialPosts.$inferSelect;

export const socialComments = pgTable("social_comments", {
  id: uuid("id").primaryKey().defaultRandom(),
  postId: uuid("post_id").notNull().references(() => socialPosts.id, { onDelete: "cascade" }),
  authorUserId: text("author_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  body: text("body").notNull(),
  mentionedUserIds: text("mentioned_user_ids").array().notNull().default(sql`'{}'::text[]`),
  likeCount: integer("like_count").notNull().default(0),
  dislikeCount: integer("dislike_count").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  postIdx: index("idx_social_comments_post").on(t.postId),
}));

export const insertSocialCommentSchema = createInsertSchema(socialComments).omit({
  id: true,
  mentionedUserIds: true,
  likeCount: true,
  dislikeCount: true,
  createdAt: true,
});
export type InsertSocialComment = z.infer<typeof insertSocialCommentSchema>;
export type SocialComment = typeof socialComments.$inferSelect;

// One reaction (like|dislike) per user per post; toggled on repeat.
export const socialReactions = pgTable("social_reactions", {
  id: uuid("id").primaryKey().defaultRandom(),
  postId: uuid("post_id").notNull().references(() => socialPosts.id, { onDelete: "cascade" }),
  userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // 'like' | 'dislike'
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  postUserUnique: unique("social_reactions_post_user_unique").on(t.postId, t.userId),
}));

export type SocialReaction = typeof socialReactions.$inferSelect;

// One reaction (like|dislike) per user per comment; toggled on repeat.
export const socialCommentReactions = pgTable("social_comment_reactions", {
  id: uuid("id").primaryKey().defaultRandom(),
  commentId: uuid("comment_id").notNull().references(() => socialComments.id, { onDelete: "cascade" }),
  userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // 'like' | 'dislike'
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  commentUserUnique: unique("social_comment_reactions_comment_user_unique").on(t.commentId, t.userId),
}));

export type SocialCommentReaction = typeof socialCommentReactions.$inferSelect;

export const socialNotifications = pgTable("social_notifications", {
  id: uuid("id").primaryKey().defaultRandom(),
  recipientUserId: text("recipient_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  actorUserId: text("actor_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // 'mention' | 'comment' | 'reaction'
  reactionType: text("reaction_type"), // 'like' | 'dislike' (only set when type = 'reaction')
  postId: uuid("post_id").references(() => socialPosts.id, { onDelete: "cascade" }),
  commentId: uuid("comment_id").references(() => socialComments.id, { onDelete: "cascade" }),
  read: boolean("read").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  recipientIdx: index("idx_social_notifications_recipient").on(t.recipientUserId, t.read),
}));

export type SocialNotification = typeof socialNotifications.$inferSelect;

// Presence of a row = user is rescinded from the social feed (read + write).
export const socialBans = pgTable("social_bans", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  bannedByUserId: text("banned_by_user_id").references(() => users.id, { onDelete: "set null" }),
  reason: text("reason"),
  createdAt: timestamp("created_at").defaultNow(),
});

export type SocialBan = typeof socialBans.$inferSelect;

// Per-user editable feed identity (display name + avatar). Absence = derive
// display name from companyName/username and show an initials avatar.
export const socialProfiles = pgTable("social_profiles", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  displayName: text("display_name"),
  avatarUrl: text("avatar_url"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertSocialProfileSchema = createInsertSchema(socialProfiles).omit({ id: true, updatedAt: true });
export type InsertSocialProfile = z.infer<typeof insertSocialProfileSchema>;
export type SocialProfile = typeof socialProfiles.$inferSelect;

// ─── Social view types ────────────────────────────────────────────────────────
export type SocialAuthor = {
  userId: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
};

export type SocialPostWithDetails = SocialPost & {
  author: SocialAuthor;
  myReaction: "like" | "dislike" | null;
};

export type SocialCommentWithDetails = SocialComment & {
  author: SocialAuthor;
  myReaction: "like" | "dislike" | null;
};

export type SocialNotificationWithDetails = SocialNotification & {
  actor: SocialAuthor;
  postPreview: string | null;
};

export type SocialBanWithDetails = SocialBan & {
  username: string;
  bannedByUsername: string | null;
};

// ─── Telemetry samples ────────────────────────────────────────────────────────
// Periodic system-metric snapshots persisted by the in-process telemetry
// sampler (server/lib/telemetry.ts). One row per metric family per interval.
// `metrics` is a flat JSONB object of numeric values only — keys ending in
// "_total" are per-interval counters (summed when time-bucketed), all other
// keys are gauges (averaged when time-bucketed). Old rows are pruned
// automatically by the sampler's retention sweep.
export const telemetrySamples = pgTable("telemetry_samples", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  family: text("family").notNull(), // 'db' | 'redis' | 'web' | 'queue' | 'ai_slots' | 'client'
  capturedAt: timestamp("captured_at").notNull().defaultNow(),
  metrics: jsonb("metrics").notNull().$type<Record<string, number>>(),
}, (table) => [
  index("idx_telemetry_samples_family_captured").on(table.family, table.capturedAt),
]);

export const insertTelemetrySampleSchema = createInsertSchema(telemetrySamples).omit({ id: true, capturedAt: true });
export type InsertTelemetrySample = z.infer<typeof insertTelemetrySampleSchema>;
export type TelemetrySample = typeof telemetrySamples.$inferSelect;

// ─── Config promotions audit ──────────────────────────────────────────────────
// One row per real (non-dry-run) cross-environment controlled-configuration
// import (scripts/config-promotion/import.ts): who promoted what and when.
export const configPromotions = pgTable("config_promotions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  promotedBy: text("promoted_by").notNull(),
  sourceEnv: text("source_env").notNull(),
  sourceHost: text("source_host").notNull(),
  targetEnv: text("target_env").notNull(),
  artifactHash: text("artifact_hash").notNull(),
  artifactExportedAt: text("artifact_exported_at"),
  dryRun: boolean("dry_run").notNull().default(false),
  tables: jsonb("tables").notNull().$type<string[]>(),
  counts: jsonb("counts").notNull().$type<Record<string, { inserted: number; updated: number; unchanged: number }>>(),
  createdAt: timestamp("created_at").defaultNow(),
});
export type ConfigPromotion = typeof configPromotions.$inferSelect;


// ═══ Potential Savings Engine (Phases 1–3, task #2379 design doc) ════════════

// Append-only provenance: every system that writes vehicle rows also records
// what it observed (floorplan sync, auto-sync, manual create, CSV import).
export const vehicleSourceRecords = pgTable("vehicle_source_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerId: varchar("dealer_id").notNull(),
  vehicleId: varchar("vehicle_id"), // inventory_items.id when resolved
  sourceType: text("source_type").notNull(), // 'floorplan_statement' | 'auto_sync' | 'manual' | 'csv_import'
  sourceDocumentId: varchar("source_document_id"), // parsed statement id when applicable
  rawVin: text("raw_vin"),
  normalizedVin: text("normalized_vin"),
  identityStatus: text("identity_status").notNull(), // 'vin_verified' | 'vin_missing' | 'unresolved'
  observedJson: jsonb("observed_json").$type<Record<string, unknown>>(),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => [
  index("idx_vehicle_source_records_dealer").on(t.dealerId, t.createdAt),
  index("idx_vehicle_source_records_vehicle").on(t.vehicleId),
]);
export type VehicleSourceRecord = typeof vehicleSourceRecords.$inferSelect;
export const insertVehicleSourceRecordSchema = createInsertSchema(vehicleSourceRecords).omit({ id: true, createdAt: true });
export type InsertVehicleSourceRecord = z.infer<typeof insertVehicleSourceRecordSchema>;

// One row per savings-analysis run. The partial unique index enforces at most
// one pending/running analysis per dealer at a time.
export const dealerAnalysisSnapshots = pgTable("dealer_analysis_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerId: varchar("dealer_id").notNull(),
  status: text("status").notNull().default("pending"), // 'pending' | 'running' | 'done' | 'error' | 'skipped'
  snapshotHash: text("snapshot_hash"),
  snapshotVersion: integer("snapshot_version"),
  triggerType: text("trigger_type"), // 'floorplan_upload' | 'bank_upload' | 'inventory_change' | ... | 'manual'
  triggerRecordIds: jsonb("trigger_record_ids").$type<string[]>(),
  correlationId: varchar("correlation_id"),
  snapshotJson: jsonb("snapshot_json").$type<Record<string, unknown>>(),
  resultJson: jsonb("result_json").$type<Record<string, unknown>>(),
  errorMessage: text("error_message"),
  startedAt: timestamp("started_at"),
  finishedAt: timestamp("finished_at"),
  createdAt: timestamp("created_at").defaultNow(),
  sourceGeneration: bigint("source_generation", { mode: "number" }),
}, (t) => [
  index("idx_dealer_analysis_snapshots_dealer").on(t.dealerId, t.createdAt),
  uniqueIndex("uq_dealer_analysis_snapshots_active").on(t.dealerId).where(sql`status IN ('pending', 'running')`),
]);
export type DealerAnalysisSnapshot = typeof dealerAnalysisSnapshots.$inferSelect;

// A continuing business concern (stable across recalculations via scope key).
export const opportunities = pgTable("opportunities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerId: varchar("dealer_id").notNull(),
  opportunityType: text("opportunity_type").notNull(), // 'aged_inventory' | 'curtailment_exposure' | ...
  scopeKey: text("scope_key").notNull(), // NEVER contains the dealer id
  status: text("status").notNull().default("new"), // 'new'|'active'|'acknowledged'|'in_progress'|'resolved'|'dismissed'|'returned'
  currentVersionId: varchar("current_version_id"),
  firstDetectedAt: timestamp("first_detected_at").notNull().defaultNow(),
  lastDetectedAt: timestamp("last_detected_at").notNull().defaultNow(),
  resolvedAt: timestamp("resolved_at"),
  dismissedAt: timestamp("dismissed_at"),
  dismissedUntil: timestamp("dismissed_until"),
  returnedAt: timestamp("returned_at"),
  recurrenceCount: integer("recurrence_count").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => [
  uniqueIndex("uq_opportunities_dealer_type_scope").on(t.dealerId, t.opportunityType, t.scopeKey),
  index("idx_opportunities_dealer_status").on(t.dealerId, t.status),
]);
export type Opportunity = typeof opportunities.$inferSelect;

// Immutable calculation versions (append-only; a material change adds a row).
export const opportunityVersions = pgTable("opportunity_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  opportunityId: varchar("opportunity_id").notNull(),
  dealerId: varchar("dealer_id").notNull(),
  snapshotId: varchar("snapshot_id"), // dealer_analysis_snapshots.id that produced it
  detectorVersion: text("detector_version").notNull(),
  formulaVersion: text("formula_version").notNull(),
  severity: text("severity").notNull(), // 'high' | 'medium' | 'low'
  confidence: text("confidence").notNull(), // 'high' | 'medium' | 'low'
  savingsClassification: text("savings_classification").notNull(),
  estimatedSavingsLow: numeric("estimated_savings_low", { precision: 12, scale: 2 }).notNull(),
  estimatedSavingsHigh: numeric("estimated_savings_high", { precision: 12, scale: 2 }).notNull(),
  savingsPeriod: text("savings_period").notNull(), // 'one_time' | 'monthly' | 'annual'
  evidenceJson: jsonb("evidence_json").$type<Array<Record<string, unknown>>>(),
  calculationJson: jsonb("calculation_json").$type<Record<string, unknown>>(),
  assumptionsJson: jsonb("assumptions_json").$type<Array<Record<string, unknown>>>(),
  affectedEntityIdsJson: jsonb("affected_entity_ids_json").$type<string[]>(),
  sourceDocumentIdsJson: jsonb("source_document_ids_json").$type<string[]>(),
  templateExplanation: text("template_explanation"),
  materialChangeReason: text("material_change_reason"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => [
  index("idx_opportunity_versions_opportunity").on(t.opportunityId, t.createdAt),
  index("idx_opportunity_versions_dealer").on(t.dealerId),
]);
export type OpportunityVersion = typeof opportunityVersions.$inferSelect;

// Impact ledger: one row per (cost category × entity × period) claim.
// Amounts are immutable once written; overlap-resolution flags (is_primary,
// excluded_from_summary, exclusion_reason, overlap_group_key, priority) are
// resolution STATE and are updated when a later run re-resolves overlaps.
export const opportunityImpacts = pgTable("opportunity_impacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerId: varchar("dealer_id").notNull(),
  opportunityId: varchar("opportunity_id").notNull(),
  versionId: varchar("version_id").notNull(),
  impactType: text("impact_type").notNull(), // 'verified_avoidable_cost' | 'estimated_operating_savings' | 'potential_upside'
  costCategory: text("cost_category").notNull(),
  affectedEntityType: text("affected_entity_type").notNull(), // 'vehicle' | 'portfolio' | 'bank_account' | 'lender'
  affectedEntityId: text("affected_entity_id").notNull(),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  amountLow: numeric("amount_low", { precision: 12, scale: 2 }).notNull(),
  amountHigh: numeric("amount_high", { precision: 12, scale: 2 }).notNull(),
  confidence: text("confidence").notNull(),
  sourceQuality: text("source_quality").notNull(), // 'documented' | 'assumption'
  scopeLevel: text("scope_level").notNull(), // 'entity' | 'portfolio'
  overlapGroupKey: text("overlap_group_key"),
  priority: integer("priority").notNull().default(0),
  isPrimary: boolean("is_primary").notNull().default(true),
  excludedFromSummary: boolean("excluded_from_summary").notNull().default(false),
  exclusionReason: text("exclusion_reason"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => [
  index("idx_opportunity_impacts_dealer").on(t.dealerId),
  index("idx_opportunity_impacts_version").on(t.versionId),
  index("idx_opportunity_impacts_overlap").on(t.dealerId, t.costCategory, t.affectedEntityType, t.affectedEntityId),
]);
export type OpportunityImpact = typeof opportunityImpacts.$inferSelect;

// Append-only status transitions for opportunity lifecycle auditability.
export const opportunityStatusEvents = pgTable("opportunity_status_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  opportunityId: varchar("opportunity_id").notNull(),
  dealerId: varchar("dealer_id").notNull(),
  fromStatus: text("from_status"),
  toStatus: text("to_status").notNull(),
  actor: text("actor").notNull(), // 'system' | user id
  reason: text("reason"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => [
  index("idx_opportunity_status_events_opportunity").on(t.opportunityId, t.createdAt),
]);
export type OpportunityStatusEvent = typeof opportunityStatusEvents.$inferSelect;

// Per-dealer assumption overrides; NULL column = fall through to profile/default.
export const dealerSavingsSettings = pgTable("dealer_savings_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerId: varchar("dealer_id").notNull().unique(),
  targetInventoryAgeDays: integer("target_inventory_age_days"),
  estimatedDailyHoldingCost: numeric("estimated_daily_holding_cost", { precision: 12, scale: 2 }),
  minimumSavingsThreshold: numeric("minimum_savings_threshold", { precision: 12, scale: 2 }),
  minimumDetectorSampleSize: integer("minimum_detector_sample_size"),
  dismissalPeriodDays: integer("dismissal_period_days"),
  materialChangeThresholdPercent: numeric("material_change_threshold_percent", { precision: 5, scale: 2 }),
  showPotentialRevenueUpside: boolean("show_potential_revenue_upside"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export type DealerSavingsSettings = typeof dealerSavingsSettings.$inferSelect;
export const insertDealerSavingsSettingsSchema = createInsertSchema(dealerSavingsSettings).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDealerSavingsSettings = z.infer<typeof insertDealerSavingsSettingsSchema>;

// ─── Website visitor tracking (self-service embeddable widget) ───────────────
// One row per dealer website being tracked. The public site key is high-entropy
// random (never derived from dealer id or URL) and rotatable/revocable.
// Designed to support multiple sites per dealer even though the initial UI
// exposes only one primary website.
export const trackedSites = pgTable("tracked_sites", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerId: varchar("dealer_id").notNull(),
  websiteUrl: text("website_url").notNull(),
  approvedDomain: text("approved_domain").notNull(), // normalized apex-or-sub host, lowercase, punycode
  siteKey: varchar("site_key", { length: 64 }).notNull().unique(),
  status: text("status").notNull().default("pending"), // 'pending' | 'connected' | 'stale' | 'revoked'
  lastEventAt: timestamp("last_event_at"),
  // Set when the "site stopped receiving visits" outage email was sent for the
  // current outage; cleared when events resume (so each outage alerts once).
  staleAlertSentAt: timestamp("stale_alert_sent_at"),
  lastRejectedReason: text("last_rejected_reason"),
  lastRejectedAt: timestamp("last_rejected_at"),
  retentionDays: integer("retention_days").notNull().default(400),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => [
  index("idx_tracked_sites_dealer").on(t.dealerId),
]);
export type TrackedSite = typeof trackedSites.$inferSelect;

// Durable per-session record. Attribution is first-touch and never overwritten
// during the session; session end is derived from lastActivityAt (30-min
// inactivity rule), never from a browser-sent "session end" event.
export const trackedSessions = pgTable("tracked_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  trackedSiteId: varchar("tracked_site_id").notNull(),
  dealerId: varchar("dealer_id").notNull(),
  visitorId: varchar("visitor_id", { length: 64 }).notNull(),
  sessionId: varchar("session_id", { length: 64 }).notNull(),
  startedAt: timestamp("started_at").notNull().defaultNow(),
  lastActivityAt: timestamp("last_activity_at").notNull().defaultNow(),
  landingUrl: text("landing_url"),
  landingPath: text("landing_path"),
  originalReferrer: text("original_referrer"),
  source: text("source").notNull().default("Direct"),
  medium: text("medium").notNull().default("direct"),
  campaign: text("campaign"),
  content: text("content"),
  term: text("term"),
  gclid: text("gclid"),
  fbclid: text("fbclid"),
  pageviewCount: integer("pageview_count").notNull().default(0),
  interactionCount: integer("interaction_count").notNull().default(0),
  isSuspectedBot: boolean("is_suspected_bot").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => [
  uniqueIndex("uq_tracked_sessions_site_session").on(t.trackedSiteId, t.sessionId),
  index("idx_tracked_sessions_site_started").on(t.trackedSiteId, t.startedAt),
  index("idx_tracked_sessions_site_source").on(t.trackedSiteId, t.source),
]);
export type TrackedSession = typeof trackedSessions.$inferSelect;

// Raw events. eventId is client-generated; the unique index provides duplicate
// suppression across sendBeacon/fetch retries. Rows are pruned after the
// site's retention window (sessions — the aggregates — are preserved).
export const trackedEvents = pgTable("tracked_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  trackedSiteId: varchar("tracked_site_id").notNull(),
  dealerId: varchar("dealer_id").notNull(),
  eventId: varchar("event_id", { length: 64 }).notNull(),
  eventType: text("event_type").notNull(), // 'pageview' | 'chat_started' | 'lead_submitted'
  visitorId: varchar("visitor_id", { length: 64 }).notNull(),
  sessionId: varchar("session_id", { length: 64 }).notNull(),
  url: text("url"),
  path: text("path"),
  isSuspectedBot: boolean("is_suspected_bot").notNull().default(false),
  ts: timestamp("ts").notNull().defaultNow(),
}, (t) => [
  uniqueIndex("uq_tracked_events_site_event").on(t.trackedSiteId, t.eventId),
  index("idx_tracked_events_site_ts").on(t.trackedSiteId, t.ts),
]);
export type TrackedEvent = typeof trackedEvents.$inferSelect;

// Append-only audit log for tracking-data lifecycle actions (key rotation,
// revocation, retention cleanup, full deletion).
export const trackingAuditEvents = pgTable("tracking_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  dealerId: varchar("dealer_id").notNull(),
  trackedSiteId: varchar("tracked_site_id"),
  action: text("action").notNull(), // 'site_created' | 'key_rotated' | 'site_revoked' | 'retention_cleanup' | 'site_deleted'
  actor: text("actor").notNull(), // user id or 'system'
  detail: text("detail"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => [
  index("idx_tracking_audit_dealer").on(t.dealerId, t.createdAt),
]);
export type TrackingAuditEvent = typeof trackingAuditEvents.$inferSelect;
